export interface GuideStep {
  title: string;
  content: string;
  tip?: string;
}

export interface Guide {
  toolId: string;
  overview: string;
  timeEstimate: string;
  prerequisites: string[];
  steps: GuideStep[];
  tips: string[];
}

export const guides: Record<string, Guide> = {
  'doximity-scribe': {
    toolId: 'doximity-scribe',
    overview: 'Doximity Scribe is a completely free AI clinical documentation tool available to all verified U.S. physicians, NPs, PAs, and medical students. It listens to your patient encounters in real-time, generates structured clinical notes, and immediately discards all audio — no recordings are ever stored. Because it\'s built into the Doximity platform, every user is automatically covered by a BAA.',
    timeEstimate: '5-10 Minutes',
    prerequisites: [
      'Verified Doximity account (free to create at doximity.com)',
      'Smartphone or computer with a working microphone',
      'Active internet connection'
    ],
    steps: [
      {
        title: 'Verify Your Doximity Account',
        content: `If you already have a Doximity account, you're good to go — just make sure you can log in. If not, here's how to get set up:

1. Go to doximity.com and click "Join" in the top-right corner
2. Enter your NPI number or medical school information
3. Complete the identity verification process
   - This usually takes 24-48 hours
   - You may need to upload a photo of your credentials
4. Once verified, you'll have full access to all Doximity tools including Scribe

Verification is required because Scribe is restricted to licensed healthcare professionals. If you're a medical student, you can verify through your school's enrollment.`,
        tip: 'If you already have a Doximity account but haven\'t logged in recently, check that your credentials still work before your next patient encounter.'
      },
      {
        title: 'Access Doximity Scribe',
        content: `There are two ways to use Scribe — pick whichever fits your workflow:

Option A — Web Browser (best for telehealth or desktop documentation):
1. Go to doximity.com/scribe in Chrome, Safari, or Edge
2. Log in with your Doximity credentials
3. You'll see the Scribe recording interface ready to go
4. Bookmark this page for quick access

Option B — Doximity Mobile App (best for in-person visits):
1. Download the Doximity app from the App Store or Google Play
2. Log in with your account credentials
3. Tap the "Scribe" tab at the bottom of the screen
4. Grant microphone permissions when prompted

Both options work the same way — choose based on whether you're documenting at a desk or carrying a phone during visits.`,
        tip: 'The mobile app works well placed on your desk during in-person visits. For telehealth, the web version can capture both sides of the conversation from your computer audio.'
      },
      {
        title: 'Record Your First Patient Encounter',
        content: `When you're ready to document a visit:

1. Open Scribe (web or app) and click/tap the "Start Recording" button
2. Place your device where it can clearly hear the conversation
   - For in-person: on your desk or in your coat pocket
   - For telehealth: the web app captures your computer audio directly
3. Conduct your patient encounter normally — speak naturally
4. When the visit is done, click/tap "Stop Recording"

Scribe processes the audio in real-time and generates your note within about 60 seconds. The audio is immediately discarded after processing — nothing is stored.

You don't need to speak in any special format. Scribe understands natural clinical conversations and structures the note accordingly.`,
        tip: 'You can pause and resume recording if you need to step out or take a phone call mid-visit. The note will still generate correctly.'
      },
      {
        title: 'Review & Edit Your Note',
        content: `After recording, Scribe generates a structured clinical note:

1. Review the generated note on screen — it typically includes:
   - Chief Complaint
   - History of Present Illness
   - Review of Systems
   - Physical Exam findings
   - Assessment & Plan
2. Click on any section to edit the text directly
3. Look for any highlighted or flagged sections that may need attention
4. Adjust the level of detail if needed — Scribe adapts to your editing patterns over time
5. Once you're happy with the note, it's ready to export

The more you use Scribe and make edits, the better it learns your personal documentation style. After a few encounters, notes will start matching your preferences more closely.`,
        tip: 'Don\'t worry if the first few notes aren\'t perfect. Scribe learns from your edits — within a week of regular use, the output quality improves significantly.'
      },
      {
        title: 'Copy Your Note To Your EMR',
        content: `Get the finished note into your patient chart:

1. Click the "Copy Note" button to copy the entire note to your clipboard
2. Open your EMR and navigate to the patient's chart
3. Paste (Ctrl+V or Cmd+V) into your documentation field
4. Save the note in your EMR

Alternative approach — copy individual sections:
- Use the "Copy Section" buttons next to each section (Chief Complaint, HPI, Assessment, etc.)
- This is useful if your EMR has separate fields for each section

If your EMR supports "smart phrases" or "dot phrases," you could create one that pastes from your clipboard for an even faster workflow.`,
        tip: 'Some clinicians keep Scribe open on one monitor and their EMR on another, copying notes between encounters. This creates a smooth documentation rhythm.'
      }
    ],
    tips: [
      'Audio is processed in real-time and immediately discarded — nothing is ever stored',
      'All Doximity users are automatically covered by a Business Associate Agreement (BAA)',
      'Scribe works best in quiet environments with clear speech',
      'You can use Scribe for telehealth visits by recording your computer audio',
      'The AI learns your documentation style over time from your edits'
    ]
  },

  'freed-ai': {
    toolId: 'freed-ai',
    overview: 'Freed is an AI medical scribe that automatically generates clinical documentation from your patient conversations. It adapts to your personal writing style over time and works with any EMR system. Freed processes audio in real-time and does not store recordings — audio is deleted immediately after note generation.',
    timeEstimate: '10-15 Minutes',
    prerequisites: [
      'Email address for account creation',
      'Computer or smartphone with a working microphone',
      'Credit card for paid plans (free trial available to start)'
    ],
    steps: [
      {
        title: 'Create Your Freed Account',
        content: `Getting started with Freed takes just a few minutes:

1. Go to getfreed.ai and click "Start Free Trial" in the top-right corner
2. Enter your email address and create a password
3. Check your inbox and click the verification link
4. Complete the onboarding questionnaire — this helps Freed tailor notes to you:
   - Your medical specialty
   - Your typical note format (SOAP, H&P, progress note, etc.)
   - Your EMR system
   - How detailed you like your notes

Freed offers a free trial so you can test it with real patient encounters before committing to a paid plan.`,
        tip: 'Use your work email if your practice will be paying for the subscription — it makes transferring the account easier later.'
      },
      {
        title: 'Install Freed On Your Devices',
        content: `Freed works across multiple platforms — set it up wherever you document:

Desktop (recommended for telehealth):
1. Go to app.getfreed.ai in Chrome, Safari, or Edge
2. Bookmark it for quick access — no installation needed
3. Allow microphone permissions when your browser prompts you

Mobile App (recommended for in-person visits):
1. Search "Freed - AI Medical Scribe" in the App Store or Google Play
2. Download and open the app
3. Log in with your account credentials
4. Grant microphone and notification permissions when prompted

Chrome Extension (optional — for one-click recording):
1. Search "Freed" in the Chrome Web Store
2. Click "Add to Chrome" to install
3. The Freed icon appears in your browser toolbar for quick access

Most clinicians use the web app at their desk and the mobile app during in-person visits.`,
        tip: 'The web app works on any device without installation. The mobile app is better for in-person visits because you can carry it in your pocket.'
      },
      {
        title: 'Configure Your Note Templates',
        content: `Freed generates notes based on templates you can customize:

1. Go to Settings → Note Templates in Freed
2. Choose your default template or create a custom one
3. Select which sections to include in your notes:
   - Chief Complaint
   - History of Present Illness
   - Review of Systems
   - Physical Exam
   - Assessment & Plan
   - Patient Instructions
4. Set your preferred verbosity level:
   - Concise: bullet points, minimal prose
   - Moderate: balanced detail
   - Detailed: comprehensive narrative
5. Add any custom sections specific to your practice or specialty

You can create multiple templates for different visit types — one for new patients, one for follow-ups, one for procedures, etc.`,
        tip: 'Freed learns from your edits over time. The more you use it and make corrections, the better it gets at matching your style. Give it a week of regular use before judging quality.'
      },
      {
        title: 'Record Your First Patient Encounter',
        content: `When you're ready to document a visit:

1. Open Freed (web or app) and click the "Start Visit" button
2. Enter the patient's name or initials
3. Select the visit type if prompted (new patient, follow-up, etc.)
4. Click "Record" to begin capturing the conversation
5. Conduct your visit normally — speak naturally, don't change your style
6. Click "End Visit" when the encounter is finished

Freed captures both sides of the conversation and automatically identifies who's speaking (clinician vs. patient). You don't need to label speakers or use any special commands.

For telehealth visits, Freed can capture audio directly from your video call software — just make sure your browser has microphone access.`,
        tip: 'Position your device where it can hear both you and the patient clearly. For in-person visits, your desk or coat pocket works well. You don\'t need to hold it up like a recorder.'
      },
      {
        title: 'Review & Finalize Your Note',
        content: `After the visit ends, Freed generates your note within 1-2 minutes:

1. Review the generated note in the Freed interface
2. Click on any section to edit text directly — your changes are saved automatically
3. Use the "Regenerate Section" button if a particular section needs rewriting
4. Check the ICD-10 code suggestions Freed provides and add relevant codes
5. Look for sections highlighted in yellow — these are low-confidence areas that need extra attention
6. Click "Finalize" when the note is ready

Remember: Freed improves by learning from your edits (not from audio — audio is never stored). Each correction teaches the AI your preferences.`,
        tip: 'Pay special attention to medication names, dosages, and specific clinical details. These are the most important areas to verify before finalizing.'
      },
      {
        title: 'Export To Your EMR',
        content: `Get your finalized note into the patient chart:

Copy/Paste Method (works with any EMR):
1. Click "Copy Note" to copy the entire note to your clipboard
2. Open your EMR, navigate to the patient's chart
3. Paste into the documentation field and save

Direct Integration (if your EMR is supported):
1. Go to Settings → Integrations in Freed
2. Find your EMR from the list and follow the connection steps
3. Once connected, notes can be sent directly to patient charts with one click

Smart Phrase Method:
If your EMR supports smart phrases or dot phrases, you can set up a workflow to paste Freed notes even faster — ask your EMR admin about this option.

Even without direct integration, copy/paste takes just a few seconds and works reliably with any EMR system.`,
        tip: 'Many clinicians find it fastest to keep Freed open alongside their EMR and copy notes between patients. The whole export process takes under 10 seconds.'
      }
    ],
    tips: [
      'Audio is processed and immediately deleted — Freed does NOT store patient recordings',
      'The AI improves over time by learning from your editing patterns',
      'Works in any language — useful for multilingual patients or interpreter encounters',
      'You can pause and resume recording mid-visit without affecting note quality',
      'Use the "Add Context" feature to include relevant background info before a visit starts'
    ]
  },

  'intakeq': {
    toolId: 'intakeq',
    overview: 'IntakeQ is a HIPAA-compliant digital intake platform built specifically for healthcare practices. It lets you create custom patient forms with conditional logic, collect e-signatures, send automated reminders, and integrate with your practice management system. Every account includes a signed BAA, so you\'re covered from day one.',
    timeEstimate: '30-45 Minutes For Basic Setup',
    prerequisites: [
      'Email address for account creation',
      'A list of what information you want to collect from patients (demographics, medical history, consent, etc.)',
      'Your practice logo (optional, for branded forms)',
      'Credit card for subscription (14-day free trial available — no card required to start)'
    ],
    steps: [
      {
        title: 'Create Your IntakeQ Account',
        content: `Getting started is straightforward:

1. Go to intakeq.com and click "Start Free Trial"
2. Enter your practice name and email address
3. Create a secure password
4. Choose your practice type from the dropdown:
   - Medical Practice
   - Mental Health
   - Dental
   - Wellness/Alternative Medicine
5. Select your timezone (this affects appointment scheduling later)
6. Check your email and click the verification link

Your account is automatically HIPAA-compliant — IntakeQ signs a BAA with every account, so you don't need to request one separately. The 14-day trial includes all features with no credit card required.`,
        tip: 'IntakeQ\'s free trial is fully featured — you can build and test all your forms before deciding to subscribe. No credit card needed to start.'
      },
      {
        title: 'Set Up Your Practice Profile',
        content: `Configure your practice details so forms look professional and branded:

1. Go to Settings → Practice Info in the top menu
2. Fill in your practice details:
   - Practice name and address
   - Phone number and email
   - Website URL
3. Upload your practice logo
   - This appears at the top of all your intake forms
   - Recommended size: at least 200x200 pixels
4. Set your business hours (used for scheduling features)
5. Configure your appointment types if you plan to use IntakeQ for scheduling

This information appears on your intake forms and in emails sent to patients. Having your logo and practice info makes forms feel trustworthy — patients are more likely to complete them.`,
        tip: 'Adding your logo takes 30 seconds but makes a big difference. Branded forms look professional and help patients trust they\'re filling out the right thing.'
      },
      {
        title: 'Create Your First Intake Form',
        content: `Build a custom intake form using the drag-and-drop editor:

1. Go to Forms → Create New Form
2. Choose your starting point:
   - "Use Template" — start with a pre-built form for your specialty (fastest)
   - "Blank Form" — build completely from scratch
3. Use the drag-and-drop builder to add fields:
   - Text fields for name, address, etc.
   - Date pickers for DOB, appointment dates
   - Checkboxes and radio buttons for yes/no questions
   - Signature fields for consent forms
   - File upload fields for insurance cards and IDs
   - Dropdown menus for selecting options
4. Add conditional logic to show/hide fields based on answers
   - Example: "If patient selects 'Yes' for allergies, show the allergy details field"
5. Arrange fields in the order that makes sense for your workflow
6. Click "Save" when finished

The form builder is visual — you can see exactly what patients will see as you build it.`,
        tip: 'Start with a template and customize it rather than building from scratch. Templates for your specialty already have the most common fields included, saving you significant setup time.'
      },
      {
        title: 'Configure Form Settings & Notifications',
        content: `Set up how your form behaves and who gets notified:

1. Click the "Settings" tab on your form
2. Configure these key settings:
   - Form title and description (what patients see at the top)
   - Completion message — the confirmation patients see after submitting
   - Expiration — how long the form link stays valid (e.g., 7 days)
3. Set up email notifications:
   - You'll receive an email whenever a patient submits the form
   - Add additional staff emails if others need to see submissions
4. Enable e-signature requirements for consent forms
   - Check "Require Signature" on any form that needs it
5. Turn on "Save and Continue" for longer forms
   - This lets patients save progress and finish later without losing their work
6. Add password protection if the form contains especially sensitive content`,
        tip: 'Enable "Save and Continue" for any form longer than one page. Patients often get interrupted and will appreciate being able to finish later without starting over.'
      },
      {
        title: 'Test Your Form Before Sending To Patients',
        content: `Always test thoroughly before going live:

1. Click "Preview" in the form builder to see the patient's view
2. Fill out the entire form yourself as a test patient
3. Submit the test form and verify:
   - Did you receive the notification email?
   - Does the submitted data appear correctly in IntakeQ?
   - Are conditional logic fields showing/hiding properly?
   - Do signature fields work?
4. Open the preview link on your phone — most patients use mobile devices
   - Check that the form is readable and easy to use on a small screen
   - Make sure buttons and fields are large enough to tap
5. Ask a colleague to test too — fresh eyes catch things you might miss
6. Make any adjustments based on what you find`,
        tip: 'Test on a phone, not just your computer. The majority of patients will fill out forms on their mobile device, so the mobile experience matters most.'
      },
      {
        title: 'Send Forms To Patients',
        content: `Get your forms in front of patients — IntakeQ offers several distribution methods:

Option A — Email to individual patients:
1. Go to Clients → Send Intake in the top menu
2. Enter the patient's email address
3. Select which form(s) to send
4. Customize the email message if you want
5. Click "Send" — the patient receives a link to complete the form

Option B — Share a public link:
1. Go to your form and click "Share"
2. Copy the public link
3. Add it to your website, include it in appointment confirmation emails, or text it to patients

Option C — Embed on your website:
1. Get the embed code from Share → Embed
2. Paste the code into your website's new patient page
3. The form appears directly on your site — patients fill it out without leaving

Most practices use a combination: email links for existing patients, and a public link or embed for new patients booking through the website.`,
        tip: 'Send intake forms 24-48 hours before appointments. This gives patients time to complete them thoughtfully, and you have the information ready before the visit.'
      },
      {
        title: 'Review Submitted Forms',
        content: `When patients submit their forms, here's how to manage the responses:

1. You'll receive an email notification for each submission
2. Go to the Clients section in IntakeQ to see all submissions
3. Click on a client's name to view their completed forms
4. Review the information and flag anything that needs follow-up before the visit
5. Print or export to PDF if you need a paper copy
6. Forms are automatically saved and fully searchable

You can also set up staff notifications so specific team members see submissions — for example, front desk for demographics, clinical staff for medical history.

All submitted data is stored securely in IntakeQ's HIPAA-compliant infrastructure and accessible whenever you need it.`,
        tip: 'Use IntakeQ\'s search function to quickly find patient forms — much faster than digging through paper files or email inboxes.'
      }
    ],
    tips: [
      'IntakeQ is HIPAA-compliant out of the box — a BAA is signed with every account automatically',
      'Forms work on any device — phone, tablet, or computer',
      'Use templates for common form types to save time building',
      'Set up automated reminders for patients who haven\'t completed their forms',
      'IntakeQ integrates with many EMRs and practice management systems',
      'You can require patients to complete forms before they can book appointments'
    ]
  },

  'scribeberry': {
    toolId: 'scribeberry',
    overview: 'Scribeberry is an AI medical scribe that auto-generates structured clinical notes for any EMR system. It claims 99.9% accuracy and includes patient intake agents, so you get both documentation and pre-visit data collection in one platform. Like most AI scribes, it processes audio in real-time and generates notes you can review and edit before finalizing.',
    timeEstimate: '15-20 Minutes',
    prerequisites: [
      'Email address for account creation',
      'Computer or smartphone with a working microphone',
      'Your EMR login credentials (needed later if you want direct integration)',
      'Credit card for subscription'
    ],
    steps: [
      {
        title: 'Create Your Scribeberry Account',
        content: `Setting up your account takes about 5 minutes:

1. Go to scribeberry.com and click "Get Started" or "Sign Up"
2. Enter your email address and create a password
3. Check your email and click the verification link
4. Complete your profile with:
   - Your name and credentials (MD, DO, NP, PA, etc.)
   - Your medical specialty
   - Your practice name
   - Which EMR system you use
5. Review and accept the HIPAA Business Associate Agreement (BAA)
6. Choose your subscription plan

If you'd like to see the platform in action before signing up, you can request a demo from the homepage.`,
        tip: 'Request a demo first if you want to compare Scribeberry against other AI scribes. The demo lets you see real note quality before committing.'
      },
      {
        title: 'Connect Your EMR (Optional But Recommended)',
        content: `Connecting your EMR lets you send notes directly to patient charts instead of copy/pasting:

1. Go to Settings → Integrations in Scribeberry
2. Find your EMR from the supported list
3. Follow the connection steps for your system:
   - Cloud-based EMRs: you'll go through an OAuth login flow (similar to "Sign in with Google")
   - On-premise EMRs: you'll need to enter API keys or credentials from your EMR admin
4. Once connected, test the sync by sending a test note
5. Configure which fields sync — notes, diagnoses, procedure codes, etc.

If your EMR isn't listed, don't worry — the copy/paste workflow works with any EMR and only takes a few extra seconds. Many users prefer copy/paste because it gives them full control over what goes into the chart.`,
        tip: 'Direct EMR integration saves time but isn\'t required. Start with copy/paste to learn the system, then add EMR integration once you\'re comfortable.'
      },
      {
        title: 'Set Up Your Note Templates',
        content: `Customize how Scribeberry structures your notes:

1. Go to Settings → Note Templates
2. Choose your primary note format:
   - SOAP notes (most common for primary care)
   - H&P — History & Physical
   - Progress notes
   - Specialty-specific templates
3. For your chosen template, customize which sections to include or exclude
4. Set your preferred level of detail:
   - Brief: concise bullet points
   - Standard: balanced detail
   - Comprehensive: thorough narrative
5. Save as your default template

You can create multiple templates for different visit types — for example, a brief template for follow-ups and a comprehensive one for new patients or complex cases.`,
        tip: 'Create separate templates for different visit types (new patient vs. follow-up vs. procedure). This produces better notes than using one template for everything.'
      },
      {
        title: 'Record Your First Patient Encounter',
        content: `When you're ready to document a visit:

1. Open Scribeberry on your device (web or app)
2. Click "New Visit" or the record button
3. Enter the patient's name or ID
4. Select the visit type and which template to use
5. Click "Record" to start capturing the conversation
6. Conduct your patient encounter naturally — no special format needed
7. Click "Stop" when the visit is finished

Scribeberry uses AI to distinguish between clinician and patient speech automatically. Position your device where it can clearly hear both of you — your desk works well for most setups.

The AI processes the audio and generates a structured note within 1-2 minutes of stopping the recording.`,
        tip: 'Place your device on your desk between you and the patient for best audio quality. You don\'t need to hold it up or speak into it directly.'
      },
      {
        title: 'Review & Edit Your Notes',
        content: `After recording, review the AI-generated note carefully:

1. Wait 1-2 minutes for processing to complete
2. Review the generated note section by section
3. Click on any section to edit the text directly
4. Pay special attention to:
   - Medication names and dosages — always double-check these
   - Specific clinical measurements and values
   - Patient-reported symptoms and timeline
5. Make any corrections needed — the system learns from your edits over time
6. Click "Finalize" when the note is ready

While Scribeberry advertises 99.9% accuracy, always verify critical clinical details. AI-generated notes are a starting point that you should review before they become part of the medical record.`,
        tip: 'Medication names and dosages are the most important things to verify. Even small errors in these details can have clinical consequences.'
      },
      {
        title: 'Set Up Patient Intake Agents',
        content: `Scribeberry includes built-in intake automation to gather patient info before visits:

1. Go to the Intake section in your Scribeberry dashboard
2. Click "Create New Form" to build a pre-visit questionnaire
3. Choose which information to collect:
   - Demographics and contact info
   - Current medications
   - Allergies
   - Reason for visit
   - Medical history updates
4. Configure when forms are sent automatically:
   - Before all appointments
   - Only for new patients
   - For specific visit types
5. Patient responses flow directly into their record
6. Before each visit, you'll see an AI summary of the intake data

This saves significant time during the actual visit because you already have the patient's history and current concerns.`,
        tip: 'Send intake forms 24-48 hours before appointments. This gives patients time to answer thoughtfully and you time to review before the visit.'
      },
      {
        title: 'Export Notes To Your EMR',
        content: `Get your finalized notes into the patient chart:

With EMR Integration (if you set it up in Step 2):
1. Click "Send to EMR" after finalizing your note
2. Confirm the patient and encounter details
3. The note appears in your EMR automatically

Without Integration (copy/paste — works with any EMR):
1. Click "Copy Note" to copy the full note to your clipboard
2. Open your EMR and navigate to the patient's chart
3. Paste into the documentation field (Ctrl+V or Cmd+V)
4. Save in your EMR

Either method takes just a few seconds. The advantage of direct integration is fewer clicks, but copy/paste gives you a chance to make any final adjustments as you paste.`,
        tip: 'Set up a keyboard shortcut for your most common EMR workflow. Many clinicians get the export process down to under 10 seconds with practice.'
      }
    ],
    tips: [
      'Scribeberry is HIPAA compliant and signs a BAA with all accounts',
      'The AI improves over time as it learns your documentation style from your edits',
      'Patient intake agents reduce time spent gathering history during visits',
      'Works with any EMR via copy/paste, even without direct integration',
      'Best audio quality comes from quiet environments with clear speech',
      'Always review AI-generated notes before finalizing — accuracy claims don\'t replace clinical judgment'
    ]
  },

  'jotform': {
    toolId: 'jotform',
    overview: 'Jotform is a versatile form builder with AI capabilities that can generate intake forms from plain-English descriptions. With over 10,000 templates, a drag-and-drop editor, and HIPAA compliance available on paid tiers, it\'s a flexible option for practices that want custom intake workflows. Jotform also offers a 50% healthcare discount on paid plans.',
    timeEstimate: '15-30 Minutes',
    prerequisites: [
      'Email address for account creation',
      'A list of what information you need to collect from patients',
      'Credit card for a HIPAA-compliant plan (the free tier is NOT HIPAA compliant)',
      'Your practice logo (optional, for branding)'
    ],
    steps: [
      {
        title: 'Create Your Jotform Account & Choose A Plan',
        content: `Important: HIPAA compliance is only available on paid plans. The free tier should NOT be used for patient data.

1. Go to jotform.com and click "Sign Up Free"
2. Create your account with your email address, or sign in with Google/Microsoft
3. Verify your email address
4. Choose your plan — here's what matters for healthcare:
   - Free: good for testing, but NOT HIPAA compliant
   - Bronze/Silver: more submissions, HIPAA compliance available
   - Gold: full features with HIPAA compliance included
5. Contact Jotform support to request the 50% healthcare discount
   - This discount isn't always shown during signup
   - Email or chat support and mention you're a healthcare practice

Do not collect any patient information until you've enabled HIPAA compliance on a paid plan.`,
        tip: 'Contact Jotform support right after signing up to ask about the 50% healthcare discount. It can save you hundreds per year and isn\'t always advertised on the pricing page.'
      },
      {
        title: 'Enable HIPAA Compliance',
        content: `Before creating any forms that collect patient information, you MUST enable HIPAA mode:

1. Go to Settings → Account → Security in your Jotform dashboard
2. Find the "HIPAA Compliance" option and enable it
3. Review and digitally sign the Business Associate Agreement (BAA)
4. Configure the required security settings:
   - Enable form encryption (all submissions encrypted at rest)
   - Set up access controls (who on your team can see submissions)
   - Enable audit logging (tracks who accessed what data and when)
5. Confirm that HIPAA mode is active — you should see a HIPAA badge on your account

Once enabled, all your forms are stored on HIPAA-compliant infrastructure with encryption. This applies to form submissions, file uploads (like insurance card photos), and any data collected.

Do NOT use Jotform for any patient health information until this step is complete and the BAA is signed.`,
        tip: 'Take a screenshot of your HIPAA compliance confirmation and BAA for your records. You may need to show proof of compliance during audits.'
      },
      {
        title: 'Create A Form With AI Or Templates',
        content: `Jotform gives you three fast ways to create forms:

Option A — AI Form Generator (fastest for custom forms):
1. Click "Create Form" → "AI Form Generator"
2. Describe what you need in plain English, for example:
   - "New patient intake form for a dermatology practice"
   - "Pre-operative questionnaire with medical history and consent"
   - "Patient satisfaction survey with rating scales"
3. The AI generates a starting form in seconds
4. Review and customize the generated fields

Option B — Templates (fastest for standard forms):
1. Click "Create Form" → "Use Template"
2. Search for healthcare templates: "patient registration," "medical history," "HIPAA consent," "telehealth consent"
3. Browse the Healthcare category for specialty-specific options
4. Preview templates before selecting one
5. Customize any template to fit your needs

Option C — Blank Form:
1. Click "Create Form" → "Blank Form"
2. Build entirely from scratch using the drag-and-drop editor

Most practices find Option A or B fastest — you can always customize after the initial creation.`,
        tip: 'Be specific when using the AI generator. "New patient intake form for dermatology with medical history, current medications, skin concerns, and photo consent" produces much better results than just "intake form."'
      },
      {
        title: 'Customize Your Form',
        content: `Refine your form with the drag-and-drop editor:

1. Add or rearrange fields from the left panel:
   - Basic: name, email, phone, address
   - Healthcare: medical history, medication list, allergies
   - Consent: signature fields, agreement checkboxes
   - File uploads: insurance cards, ID photos, referral documents
2. Mark which fields are required (the red asterisk)
3. Add conditional logic to keep forms smart and short:
   - Example: only show allergy details if the patient selects "Yes" for allergies
   - Example: show pregnancy questions only for patients who select "Female"
4. Customize the visual design:
   - Add your practice logo at the top
   - Match your brand colors
   - Choose a clean, readable layout
5. Preview on both desktop and mobile views
   - Click the mobile preview button to check the phone experience

Conditional logic is one of Jotform's strongest features — it lets you create one form that adapts to each patient instead of having separate forms for different scenarios.`,
        tip: 'Use conditional logic aggressively to keep forms short. Patients are more likely to complete a form that only shows relevant questions. A 30-field form that conditionally shows 15 feels much shorter than a flat 15-field form.'
      },
      {
        title: 'Set Up Form Distribution',
        content: `Get your form to patients through multiple channels:

Option A — Direct Link (simplest):
1. Click "Publish" on your form
2. Copy the form URL
3. Share via email, text message, or add to your website

Option B — Embed on Your Website:
1. Go to Publish → Embed
2. Copy the embed code
3. Paste into your website's HTML
   - WordPress: use an HTML block
   - Squarespace: use a Code Block
   - Wix: use an HTML Embed element

Option C — QR Code (great for your waiting room):
1. Go to Publish → QR Code
2. Download the QR code image
3. Print and display at your front desk or in exam rooms
4. Patients scan with their phone camera and fill out the form on their device

Option D — Email Integration:
1. Set up automated form sending when appointments are booked
2. Connect with your scheduling system if supported

Most practices use a combination: embed on the website for new patients, email links for existing patients, and a QR code in the office as a backup.`,
        tip: 'Print a QR code and place it at your front desk. Patients who arrive without completing their forms can scan it and fill everything out on their phone while waiting.'
      },
      {
        title: 'Manage Submissions & Set Up Notifications',
        content: `Handle incoming form submissions efficiently:

1. View all submissions in the Jotform inbox — each entry shows the patient's responses
2. Set up email notifications so you're alerted when forms come in:
   - Go to Settings → Emails on your form
   - Add notification emails for yourself and relevant staff
   - Customize what information appears in the notification email
3. Export data when needed:
   - PDF for individual patient records
   - Excel/CSV for batch processing or importing into your PMS
   - Direct integration with your EMR if supported
4. Use filters and search to find specific submissions quickly
5. Set up automated workflows:
   - Send confirmation emails to patients after they submit
   - Route submissions to specific team members based on form type
   - Set reminders for patients who started but didn't finish

Submissions are stored securely in Jotform's HIPAA-compliant infrastructure as long as your account is active.`,
        tip: 'Set up integrations with Google Sheets or your EMR to automatically sync submissions. This eliminates manual data entry and reduces errors.'
      }
    ],
    tips: [
      'HIPAA compliance requires a paid plan — the free tier is NOT suitable for patient data',
      'Always request the 50% healthcare discount — it\'s available but not always advertised',
      'The AI form generator can save hours compared to manual form building',
      'Use conditional logic to create smart, adaptive forms that feel short',
      'Test all forms on mobile — most patients will use their phones',
      'Set up automated reminders for patients who start but don\'t finish forms',
      'Jotform integrates with 100+ apps including many EMR systems'
    ]
  },

  'infermedica': {
    toolId: 'infermedica',
    overview: 'Infermedica provides enterprise-grade AI-powered symptom collection and pre-visit triage. Its clinical algorithms guide patients through a structured symptom assessment before their visit, capturing comprehensive information that can reduce average visit times from 20 minutes to 12.5 minutes. This is enterprise software — expect a sales process, dedicated implementation support, and pricing tailored to your organization.',
    timeEstimate: '10,000-20,000 Minutes',
    prerequisites: [
      'Healthcare organization with IT resources for integration work',
      'Budget approved for enterprise software',
      'A dedicated implementation team or point person',
      'EMR/EHR system that supports integration (API access)',
      'Executive sponsorship for the project'
    ],
    steps: [
      {
        title: 'Request A Demo & Evaluate Fit',
        content: `Infermedica is not self-service software — you'll work with their sales and implementation teams:

1. Go to infermedica.com/solutions/intake
2. Click "Request Demo" or "Contact Sales"
3. Fill out the contact form with:
   - Your organization name and size (number of providers, locations)
   - Your role and contact information
   - What challenges you're trying to solve (long visit times, incomplete intake, triage accuracy)
   - Estimated monthly patient volume
4. A sales representative will schedule a 30-45 minute demo call
5. During the demo, they'll show you:
   - How the patient-facing symptom checker works
   - What clinicians see in their dashboard
   - Integration options with your EMR
   - Reporting and analytics capabilities

Come prepared with specific metrics you want to improve (average visit time, no-show rate, intake completion rate) — this helps the Infermedica team show you relevant use cases.`,
        tip: 'Bring your IT lead and a clinical champion to the demo call. The IT person can assess integration complexity, and the clinician can evaluate whether the symptom algorithms match your clinical workflows.'
      },
      {
        title: 'Plan Your Implementation',
        content: `If you decide to move forward, you'll work with Infermedica to plan the rollout:

1. Review technical requirements with your IT team:
   - API documentation and integration architecture
   - Data flow between Infermedica, your patient portal, and your EMR
   - Security and compliance requirements
2. Define your use cases — Infermedica can do several things:
   - Pre-visit symptom collection (patients complete before appointment)
   - Real-time triage and routing (direct patients to the right provider/urgency)
   - Clinical decision support (surface relevant information for clinicians)
3. Choose a pilot scope:
   - Start with one department or location
   - Select a patient population where intake is most painful
   - Define clear success metrics (time saved per visit, completion rate, staff satisfaction)
4. Establish a timeline with milestones
5. Assign internal resources — you'll need IT support, a clinical champion, and a project manager

Most organizations take 1-2 weeks for the initial pilot, with full rollout happening over 1-3 months.`,
        tip: 'Start with a pilot in one department before rolling out organization-wide. This lets you iron out workflows and gather feedback without risking disruption across the whole practice.'
      },
      {
        title: 'Complete Technical Integration',
        content: `Your IT team works with Infermedica engineers to connect the systems:

1. Review the API documentation and integration guides Infermedica provides
2. Set up secure connections between:
   - Infermedica → your patient portal (where patients complete the intake)
   - Infermedica → your EMR (where clinicians see the results)
   - Your scheduling system → Infermedica (to trigger intake at the right time)
3. Configure patient data flow:
   - Symptom data from intake → structured summary in EMR
   - Triage recommendations → care team notifications
   - Pre-visit summaries → clinician dashboard
4. Implement authentication (SSO if your organization uses it)
5. Set up HIPAA-compliant data handling — encryption in transit and at rest
6. Conduct security review and testing with your IT security team

Infermedica's implementation team will guide you through each step and provide support for any integration challenges.`,
        tip: 'Involve your IT security team from the start. Healthcare integrations require careful security planning, and getting security sign-off early prevents delays later.'
      },
      {
        title: 'Configure Clinical Content & Triage Rules',
        content: `Customize Infermedica for your organization's clinical workflows:

1. Review the symptom algorithms and clinical pathways with your clinical team
2. Set triage thresholds appropriate for your patient population:
   - What severity triggers urgent care routing?
   - Which symptoms should route to specialists?
   - Which cases can be handled via telehealth?
3. Customize patient-facing language:
   - Adjust reading level (Infermedica supports plain language)
   - Add your practice branding
   - Customize instructions and messaging
4. Configure specialty-specific pathways if needed
5. Set up routing rules:
   - High-urgency symptoms → immediate notification to care team
   - Specialty-specific symptoms → route to appropriate specialist
   - Routine concerns → standard appointment preparation
6. Review clinical decision support alerts with your medical director

Your clinical champion should validate that the AI recommendations align with your practice's care protocols.`,
        tip: 'Have multiple clinicians review the triage rules, not just one person. Different providers may have different perspectives on urgency thresholds and routing logic.'
      },
      {
        title: 'Train Staff & Run Your Pilot',
        content: `Prepare your team and start with a controlled rollout:

1. Train each role on their part of the new workflow:
   - Front desk: how patients are invited to complete intake, troubleshooting
   - Clinical staff: reviewing AI-generated summaries, understanding triage scores
   - IT support: troubleshooting integration issues, monitoring system health
2. Create quick reference guides for common scenarios
3. Launch the pilot with your selected department:
   - Start with a small number of patients per day
   - Have extra support available for the first few days
4. Monitor key metrics daily during the pilot:
   - Patient completion rates (are patients finishing the intake?)
   - Time savings per visit
   - Staff and clinician satisfaction
   - Triage accuracy (do routing recommendations make sense?)
5. Gather feedback from patients and staff after each day
6. Adjust workflows based on what you learn

Infermedica typically provides dedicated support during the pilot phase.`,
        tip: 'Identify champions in each department who are excited about the technology. They\'ll help train colleagues and troubleshoot issues faster than a top-down rollout.'
      },
      {
        title: 'Full Deployment & Optimization',
        content: `Once the pilot proves successful, expand across your organization:

1. Document the lessons learned from the pilot — what worked, what needed adjustment
2. Expand to additional departments or locations in phases
3. Continue monitoring and optimizing:
   - Review patient completion rates and look for drop-off points
   - Track time savings per visit and compare to baseline
   - Monitor patient satisfaction scores
   - Check triage accuracy and adjust thresholds as needed
4. Schedule regular reviews with your Infermedica account team
5. Stay updated on new features and clinical content updates
6. Track and report ROI to maintain executive support:
   - Visit time reduction
   - Staff efficiency gains
   - Patient satisfaction improvements
   - Clinical outcome data if available

Full deployment usually takes 1-3 months from the end of the pilot, depending on organization size and complexity.`,
        tip: 'Track and report ROI metrics quarterly to maintain executive support and budget. Concrete numbers (e.g., "7.5 minutes saved per visit across 200 daily visits") are more compelling than general satisfaction scores.'
      }
    ],
    tips: [
      'Infermedica is enterprise software — expect a significant annual investment and a consultative sales process',
      'Implementation typically takes 1-2 weeks minimum, often longer for complex organizations',
      'The AI is clinically validated but should support, not replace, clinical judgment',
      'Best results come from tight EMR integration — make this a priority',
      'Start with high-volume, straightforward use cases for the quickest wins',
      'Patient adoption is critical — make the intake experience user-friendly and test on mobile',
      'Regularly review clinical pathways as medical guidelines evolve'
    ]
  },

  'kommunicate': {
    toolId: 'kommunicate',
    overview: 'Kommunicate is an AI chatbot platform that lets you build a custom bot trained on your own documents — FAQs, office policies, procedure information, and more. With HIPAA compliance and built-in bot-to-human handoff, it can handle routine patient questions 24/7 while ensuring complex issues reach your staff. Think of it as a front desk assistant that never sleeps.',
    timeEstimate: '60-120 Minutes',
    prerequisites: [
      'Email address for account creation',
      'Documents to train the bot on (FAQ pages, office policies, procedure info, insurance details)',
      'Website where you\'ll deploy the chatbot widget',
      'Credit card for subscription (~$100/month for the HIPAA-compliant healthcare tier)'
    ],
    steps: [
      {
        title: 'Create Your Account & Enable HIPAA Compliance',
        content: `Set up your Kommunicate account with healthcare compliance from the start:

1. Go to kommunicate.io and click "Start Free Trial"
2. Sign up with your email address
3. Complete your profile:
   - Organization name (your practice name)
   - Industry: select "Healthcare"
   - Team size
4. Verify your email
5. Choose the HIPAA-compliant plan — this is required for healthcare use
   - Look for the plan that includes HIPAA compliance and BAA
   - If you're unsure which plan, contact their sales team
6. Review and sign the Business Associate Agreement (BAA)

Do not add any patient-related content or deploy the chatbot until the BAA is signed and HIPAA compliance is confirmed on your account.`,
        tip: 'Make sure you\'re on a HIPAA-compliant plan before adding any healthcare content. The basic plans may not include HIPAA compliance — confirm with Kommunicate support if you\'re unsure.'
      },
      {
        title: 'Build Your Bot\'s Knowledge Base',
        content: `Train your chatbot on your practice's information — this is what determines how helpful it is:

1. Go to Bot → Knowledge Base in the Kommunicate dashboard
2. Add your content sources:
   - Upload documents: PDFs, Word docs of your policies and procedures
   - Add FAQ pages: paste URLs from your website
   - Enter Q&A pairs manually: type in common questions and answers
3. Start with the questions patients ask most often:
   - "What are your office hours?"
   - "Do you accept my insurance?"
   - "How do I schedule an appointment?"
   - "What should I bring to my first visit?"
   - "What's your cancellation policy?"
   - "Do you offer telehealth?"
   - Pre-appointment instructions for common procedures
   - Post-procedure care instructions
4. Review how the bot processes and understands your content
5. Test by asking questions and checking the responses

The more content you add, the more helpful the bot becomes. Start with the top 20 questions your front desk answers repeatedly.`,
        tip: 'Ask your front desk staff to keep a tally of patient questions for a week before setting up the bot. This tells you exactly what content to prioritize — the questions they answer 10+ times a day are your biggest time-savers.'
      },
      {
        title: 'Configure Bot Personality & Responses',
        content: `Fine-tune how your chatbot communicates with patients:

1. Go to Bot → Conversation Settings
2. Set the bot's identity:
   - Name: something professional like "Care Assistant" or "[Practice Name] Assistant"
   - Tone: professional but friendly (match your practice's vibe)
   - Avatar: upload your practice logo or choose a professional icon
3. Configure response behavior:
   - What happens when the bot doesn't know an answer? (e.g., "I'm not sure about that — let me connect you with our team")
   - Set confidence thresholds — how sure the bot needs to be before giving an answer
   - When should it automatically escalate to a human? (low confidence, certain keywords, etc.)
4. Set up a welcome message — the first thing patients see:
   - Example: "Hi! I'm [Practice Name]'s virtual assistant. I can help with scheduling, office information, and general questions. How can I help you today?"
5. Create quick reply buttons for common topics:
   - "Schedule an appointment"
   - "Office hours & location"
   - "Insurance information"
   - "Talk to someone"`,
        tip: 'Test the bot\'s responses yourself before going live. Have several staff members chat with it and note any responses that feel wrong, unhelpful, or off-brand. Fix these before patients see them.'
      },
      {
        title: 'Set Up Human Handoff',
        content: `Configure when and how conversations transfer from the bot to your staff — this is critical for patient trust:

1. Go to Settings → Routing in the Kommunicate dashboard
2. Define escalation triggers — conversations should transfer to a human when:
   - The patient uses keywords like "emergency," "urgent," or "speak to someone"
   - The bot's confidence is below your threshold
   - The patient explicitly requests a human
   - The conversation involves scheduling or appointment changes
   - The topic involves clinical questions the bot shouldn't answer
3. Set your business hours for live support:
   - During hours: route to available staff in real-time
   - After hours: show a message like "Our team is available Mon-Fri 8am-5pm. Leave your name and question, and we'll get back to you first thing."
4. Configure after-hours options:
   - "Leave a message" form
   - Email notification to staff
   - Callback request option
5. Set up agent assignments — who receives handoff requests
6. Test the handoff flow end-to-end: trigger an escalation and verify a staff member receives it`,
        tip: 'Always provide a clear, easy path to reach a human. Patients get frustrated quickly when they feel trapped talking to a bot that can\'t help them. The "Talk to someone" button should be visible at all times.'
      },
      {
        title: 'Customize The Chat Widget & Install On Your Website',
        content: `Brand the chatbot to match your practice and deploy it:

Customize the widget:
1. Go to Settings → Chat Widget
2. Set your brand colors, widget position (bottom-right corner works best), and size
3. Add your practice logo
4. Set the chat button text (e.g., "Chat with us" or "Need help?")
5. Configure a pre-chat form if you want to collect the patient's name and reason for contact
6. Preview on both desktop and mobile to make sure it looks right

Install on your website:
1. Go to Settings → Install
2. Copy the JavaScript snippet provided
3. Add it to your website:
   - Paste the code before the closing </body> tag in your site's HTML
   - Or use Google Tag Manager to add it without touching code
   - Or install the WordPress plugin if you're on WordPress
4. Refresh your website and verify the chat widget appears
5. Test a conversation on your live site
6. Verify that conversations show up in your Kommunicate dashboard`,
        tip: 'Place the widget in the bottom-right corner — this is where users expect to find chat support. Test on multiple devices and browsers to make sure the experience is consistent.'
      },
      {
        title: 'Monitor, Review, & Improve',
        content: `Your chatbot gets better over time as you review conversations and add content:

1. Check the conversation logs in your dashboard daily for the first week
2. Look for patterns:
   - Questions the bot answers well → great, keep going
   - Questions the bot struggles with → add more content to the knowledge base
   - Questions that always escalate to humans → consider whether the bot could handle these with better training
3. Track key metrics:
   - Bot resolution rate (what percentage of conversations are resolved without human help?)
   - Handoff rate (how often do patients need a human?)
   - Patient satisfaction (if you have feedback enabled)
   - Response accuracy
4. Set up a weekly 15-minute review to improve responses
5. Update the knowledge base whenever your practice information changes (new hours, new providers, policy updates)

Most chatbots reach peak effectiveness after 2-3 weeks of active monitoring and improvement.`,
        tip: 'Review conversation logs weekly, especially in the first month. You\'ll quickly see which questions need better answers and what content to add next.'
      }
    ],
    tips: [
      'HIPAA compliance is essential — never use a non-compliant tier for patient interactions',
      'Start with a focused scope (top 20 FAQs) and expand as the bot proves effective',
      'Train all staff who handle handoff conversations on the dashboard',
      'The bot should augment human interaction, not replace it for complex or clinical issues',
      'Update the knowledge base whenever your practice information changes',
      'Monitor frustrated conversations closely and improve those flows first',
      'Consider adding the chat widget to your patient portal as well as your website'
    ]
  },

  'bastiongpt': {
    toolId: 'bastiongpt',
    overview: 'BastionGPT is a HIPAA-compliant version of ChatGPT built specifically for healthcare. Unlike regular ChatGPT, your data is never sent to OpenAI\'s standard API, never used for AI training, and never accessible to anyone outside your organization. This makes it one of the few AI chat tools where you can safely discuss patient information, upload clinical documents, and use AI assistance with PHI.',
    timeEstimate: '10-15 Minutes',
    prerequisites: [
      'Email address for account creation',
      'Credit card for subscription',
      'Understanding of your practice\'s PHI handling requirements'
    ],
    steps: [
      {
        title: 'Create Your BastionGPT Account',
        content: `Getting started takes just a few minutes:

1. Go to bastiongpt.com and click "Sign Up" or "Get Started"
2. Enter your email and create a password
3. Verify your email address by clicking the confirmation link
4. Complete your profile:
   - Your name and role
   - Organization/practice name
   - How you plan to use BastionGPT (clinical documentation, research, admin tasks, etc.)
5. Review and digitally sign the Business Associate Agreement (BAA)
   - This is required before you can use the platform
   - BastionGPT provides the BAA as part of the signup flow
6. Select your subscription plan and enter payment information

Once the BAA is signed, you're cleared to use BastionGPT with patient health information.`,
        tip: 'Save a copy of your signed BAA for your compliance records. You may need to provide proof of a signed BAA during HIPAA audits.'
      },
      {
        title: 'Understand The Security Model',
        content: `Before using BastionGPT with patient data, understand how it protects your information:

What makes BastionGPT different from regular ChatGPT:
- Your data is NOT sent through OpenAI's standard API endpoints
- Your conversations are NOT used to train AI models
- All data is encrypted in transit and at rest
- Access controls ensure only authorized users see data
- Audit logging tracks all interactions for compliance

What this means for your practice:
- You CAN discuss specific patient information
- You CAN upload documents that contain PHI (lab reports, clinical notes, etc.)
- You CAN ask clinical questions with patient context
- Your data remains private and is never shared or used for model training

Keep BastionGPT's security documentation accessible — your compliance officer or IT team may need it for audits.`,
        tip: 'BastionGPT\'s security documentation is useful during compliance audits. Bookmark the page or save a PDF of their security overview for your records.'
      },
      {
        title: 'Set Up Your Workspace',
        content: `Configure BastionGPT to work efficiently for your daily tasks:

1. Log in and access your dashboard
2. Set up your preferences:
   - Default AI model (GPT-4 is recommended for clinical tasks — it's more accurate)
   - Response style: choose between concise or detailed answers
   - Add specialty context if the option is available
3. Organize your workspace with conversation folders:
   - Create categories like "Patient Consultations," "Research," "Administrative," "Patient Education"
   - This makes it easy to find past conversations later
4. If you're on a team plan, set up access for other authorized users
   - Add colleagues who need HIPAA-compliant AI access
   - Configure permissions based on role

A well-organized workspace saves time when you need to reference past conversations or share insights with colleagues.`,
        tip: 'Create separate conversation threads for different patients or topics. This keeps things organized and makes it easy to find past interactions when you need them.'
      },
      {
        title: 'Learn Effective Prompting For Healthcare',
        content: `BastionGPT is most valuable when you give it specific, detailed prompts. Here are proven use cases:

Drafting patient communications:
"Draft a follow-up letter for a 65-year-old patient who had a right total knee replacement 2 weeks ago. Include expected recovery milestones for weeks 2-6 and warning signs that should prompt an ER visit."

Summarizing clinical information:
"Summarize the key findings from this radiology report in language I can use to explain it to the patient: [paste report]"

Research assistance:
"What are the current ADA guidelines for managing Type 2 diabetes in patients over 65 who also have stage 3 CKD?"

Prior authorization support:
"Help me draft a prior authorization appeal for [procedure] based on these clinical details: [paste details]. Include medical necessity justification."

Patient education materials:
"Create a take-home instruction sheet for a patient starting metformin, written at a 6th-grade reading level."

The key is being specific — include relevant clinical context, the audience, and what format you want the output in.`,
        tip: 'Be specific in your prompts and provide relevant clinical context. "Summarize this report" gets an okay answer. "Summarize this report\'s key findings in 3 bullet points I can discuss with the patient" gets a great answer.'
      },
      {
        title: 'Use Document Analysis For Complex Cases',
        content: `BastionGPT can analyze uploaded clinical documents — this is especially powerful for complex cases:

1. Upload documents directly in the chat window:
   - Clinical notes and progress notes
   - Lab reports and imaging results
   - Research papers and clinical guidelines
   - Insurance documents and policy information
2. Ask specific questions about the content:
   - "Summarize the key findings from this pathology report"
   - "Extract the complete medication list with dosages"
   - "Identify any abnormal lab values and explain their clinical significance"
   - "Compare these two progress notes and highlight what changed"
3. Use it for complex case preparation:
   - Upload multiple documents for a complex patient
   - Ask BastionGPT to synthesize the information
   - Generate discussion points for case conferences
4. Create patient-facing summaries from clinical documents

Document analysis is particularly valuable when preparing for complex cases with extensive medical histories.`,
        tip: 'For complex cases, upload all relevant documents at once and ask BastionGPT to synthesize them. "Given these 3 documents, what are the key clinical concerns and what additional workup might be needed?" is a powerful prompt.'
      },
      {
        title: 'Build BastionGPT Into Your Daily Workflow',
        content: `Make BastionGPT a natural part of your clinical day:

1. Bookmark bastiongpt.com for instant access — keep it open in a browser tab during documentation time
2. Common daily uses:
   - Morning: review complex cases and prepare discussion points
   - During visits: quick clinical reference (drug interactions, guideline lookups)
   - After visits: draft notes, letters, and patient education materials
   - End of day: prior authorization support and administrative tasks
3. Build a library of your most useful prompts:
   - Save prompts that produce great results
   - Create templates for tasks you do repeatedly
   - Share effective prompts with colleagues on team plans
4. Copy outputs to your EMR as needed — review and edit before finalizing
5. Use it for continuing education — ask questions about new guidelines, research, or clinical scenarios

Remember: always review AI-generated clinical content before using it in patient care. BastionGPT is a powerful tool that augments your expertise — it doesn't replace clinical judgment.`,
        tip: 'Create a "prompt library" — a simple document with your best prompts for common tasks. This saves time and ensures consistent quality when you\'re using BastionGPT under time pressure.'
      }
    ],
    tips: [
      'BastionGPT is one of the few AI chat tools safe for PHI — use this advantage for clinical workflows',
      'Always review AI-generated clinical content before using it in patient care',
      'The AI is a tool to augment your expertise, not a replacement for clinical judgment',
      'Specific, detailed prompts produce much better outputs than vague ones',
      'Document analysis is powerful for synthesizing complex cases with multiple records',
      'Keep up with new features — AI capabilities are evolving rapidly',
      'Great for drafting — let it create first drafts of letters, notes, and materials, then you edit'
    ]
  },

  'nexhealth': {
    toolId: 'nexhealth',
    overview: 'NexHealth is a patient experience platform that brings online booking, automated reminders, two-way messaging, reviews, and EHR sync into one dashboard. It connects directly to your existing practice management system so appointments, patient records, and communications stay in sync without manual entry.',
    timeEstimate: '60-120 Minutes',
    prerequisites: [
      'Practice management system or EHR (e.g. Dentrix, Open Dental, Eaglesoft, eClinicalWorks, athenahealth)',
      'Admin-level access to your PMS/EHR',
      'Practice website where you can add a booking widget or link',
      'List of appointment types and provider schedules'
    ],
    steps: [
      {
        title: 'Request A NexHealth Demo',
        content: `NexHealth uses a guided onboarding process — you'll work with their team to get set up.

1. Go to nexhealth.com and click "Get a Demo" in the top-right corner
2. Fill out the form with your practice name, number of locations, and PMS/EHR system
3. A NexHealth rep will schedule a 20-30 minute walkthrough call
4. During the demo, ask about:
   - Integration with your specific PMS/EHR
   - Pricing for your practice size
   - Timeline for going live
   - HIPAA BAA (they'll send one to sign)

After the demo, if you decide to move forward, they'll assign you an onboarding specialist.`,
        tip: 'Write down your current pain points before the demo (e.g. "patients call to book instead of going online", "we manually send reminders"). This helps the rep tailor the setup to your needs.'
      },
      {
        title: 'Connect Your Practice Management System',
        content: `NexHealth integrates directly with your PMS/EHR. Your onboarding specialist will walk you through this, but here's what to expect:

1. You'll install a small NexHealth Sync app on the computer that runs your PMS
   - This is usually your front desk workstation or server
   - The installer takes about 5 minutes
2. Log into your PMS with admin credentials when prompted
3. NexHealth will pull in your:
   - Provider list and schedules
   - Appointment types
   - Patient records
   - Operatory/room setup (for dental)

The sync runs continuously in the background. New appointments booked through NexHealth appear in your PMS automatically, and changes in your PMS reflect in NexHealth.`,
        tip: 'If your PMS runs on a cloud/server setup rather than a single workstation, let your onboarding specialist know — the install process is slightly different.'
      },
      {
        title: 'Set Up Online Booking',
        content: `Configure which appointment types patients can book online:

1. In NexHealth, go to Settings → Online Booking
2. For each appointment type, decide:
   - Should patients be able to book this online? (e.g. "New Patient Exam" yes, "Emergency" no)
   - How far in advance can they book? (e.g. up to 90 days out)
   - Any buffer time between appointments?
3. Set provider availability
   - NexHealth pulls your existing schedules from your PMS
   - You can add additional blocking if needed (e.g. no online bookings during lunch)
4. Customize the booking widget appearance
   - Add your practice logo and colors
   - Preview how it looks on desktop and mobile

NexHealth gives you two ways to add booking to your website:
- An embeddable widget (a button or inline calendar on your site)
- A hosted booking page (a link like yourpractice.nexhealth.com)`,
        tip: 'Start with just 2-3 appointment types for online booking. You can always add more once you see how patients use it.'
      },
      {
        title: 'Configure Automated Reminders',
        content: `Set up automated appointment reminders to reduce no-shows:

1. Go to Settings → Reminders in NexHealth
2. Choose your reminder schedule — most practices use:
   - 48 hours before: Email + text reminder
   - 2 hours before: Text-only reminder
3. Customize the message templates
   - Add your practice name and phone number
   - Include a confirm/cancel link (NexHealth adds this automatically)
   - Keep messages short and clear
4. Enable two-way texting so patients can reply directly
   - Replies go to your NexHealth inbox
   - Front desk can respond from the dashboard

You can also set up:
- Recall reminders (e.g. "Time for your 6-month cleaning")
- Post-visit follow-ups
- Review requests (sent after appointments)`,
        tip: 'Enable the "confirm/cancel" feature in reminders — it lets patients confirm with one tap, and cancellations automatically open the slot for waitlist patients.'
      },
      {
        title: 'Add Your Booking Link To Your Website',
        content: `Get the booking widget live on your practice website:

Option A — Embed the widget directly:
1. In NexHealth, go to Settings → Online Booking → Website Integration
2. Copy the embed code snippet
3. Send it to your web developer or paste it into your website builder:
   - WordPress: Add an HTML block and paste the code
   - Squarespace: Use a Code Block in the page editor
   - Wix: Use the HTML Embed element
4. Place it on your homepage and/or a dedicated "Book Online" page

Option B — Use a booking link:
1. Copy your hosted booking page URL from NexHealth
2. Add a "Book Online" button anywhere on your site that links to it
3. You can also add this link to:
   - Your Google Business Profile
   - Social media bios
   - Email signatures
   - Patient communications`,
        tip: 'Add the booking link to your Google Business Profile — many patients find practices through Google and will book directly from there.'
      },
      {
        title: 'Test Everything Before Going Live',
        content: `Run through the full patient experience before announcing online booking:

1. Book a test appointment through your website widget
   - Verify it shows up in your PMS
   - Check that the confirmation email/text is sent
2. Test the reminder flow
   - Create a test appointment for tomorrow
   - Verify reminders are sent at the right times
3. Test two-way messaging
   - Send a message from the NexHealth dashboard
   - Reply from a test phone number
4. Have a staff member try the patient-facing booking flow
   - Note anything confusing and adjust labels/instructions
5. Check your NexHealth dashboard
   - Make sure all providers and appointment types look correct
   - Review the analytics section

Once everything checks out, you're ready to start directing patients to book online.`,
        tip: 'Create a short announcement for existing patients: "You can now book appointments online! Visit [your booking link] or click Book Online on our website."'
      }
    ],
    tips: [
      'Start with online booking for just new patient appointments to keep things simple',
      'Use the waitlist feature to automatically fill cancelled slots',
      'Review your NexHealth analytics weekly to see booking trends and no-show rates',
      'Train all front desk staff on the dashboard so anyone can manage messages and appointments',
      'Ask NexHealth support about their forms feature if you also want digital intake'
    ]
  },

  'emitrr': {
    toolId: 'emitrr',
    overview: 'Emitrr is an all-in-one patient communication platform that combines AI-powered scheduling, VoIP phone service, two-way texting, automated reminders, and a chatbot into one affordable package. It\'s designed for small to medium practices that want to consolidate their communication tools without paying enterprise prices.',
    timeEstimate: '30-45 Minutes',
    prerequisites: [
      'Email address for account creation',
      'Your practice phone number (you can port your existing number or get a new one)',
      'List of appointment types and provider schedules',
      'Names of staff who will use the system'
    ],
    steps: [
      {
        title: 'Sign Up For Emitrr',
        content: `Getting started is quick and straightforward:

1. Go to emitrr.com and click "Get Started" or "Start Free Trial"
2. Enter your practice information:
   - Practice name
   - Your name and role
   - Email and phone number
   - Practice size (number of providers)
3. Select which features you're interested in:
   - Appointment scheduling
   - Two-way texting
   - VoIP phone system
   - Automated reminders
   - AI chatbot
4. Complete signup and verify your email

Emitrr is known for being affordable compared to enterprise solutions like NexHealth or Weave. During signup or your first call with their team, ask about pricing for your practice size.`,
        tip: 'Emitrr is one of the more affordable options in this category — ask about pricing for small practices specifically. They often have packages tailored to 1-5 provider offices.'
      },
      {
        title: 'Set Up Your Phone System',
        content: `Configure Emitrr's VoIP phone service — this replaces or supplements your existing phone system:

1. Choose your phone setup:
   - Port your existing number: keeps the number patients already know (recommended)
   - Get a new number: Emitrr assigns you a local number instantly
2. If porting your existing number:
   - Provide your current carrier information
   - Authorize the port transfer
   - Wait for the transfer to complete (can take 1-5 business days)
   - Emitrr gives you a temporary number to use in the meantime
3. Set up your phone tree (IVR — what callers hear when they dial in):
   - "Press 1 for appointments"
   - "Press 2 for billing"
   - "Press 3 for prescription refills"
   - After-hours routing: voicemail, answering service, or emergency line
4. Configure voicemail greetings
5. Test incoming and outgoing calls to make sure everything sounds right

VoIP means your staff can make and receive calls from the Emitrr app on their phones or computers — not just from the front desk phone.`,
        tip: 'Keep your existing phone number if patients know it — porting takes a few days but avoids the confusion of giving everyone a new number. The temporary number bridges the gap.'
      },
      {
        title: 'Configure Two-Way Texting',
        content: `Set up SMS communication with patients — this is often the most-used feature:

1. Go to Settings → Messaging in Emitrr
2. Your phone number is automatically text-enabled (same number patients call)
3. Set up auto-responses for common scenarios:
   - Business hours acknowledgment: "Thanks for your message! We'll respond within [timeframe]."
   - After-hours: "Our office is currently closed. We're available Mon-Fri 8am-5pm. For emergencies, please call 911."
   - Standard keywords: STOP (opt-out), HELP (support info)
4. Create reusable message templates for common texts:
   - Appointment confirmations
   - Reminder messages
   - Follow-up instructions
   - Balance notifications
5. If you have a landline, enable text-to-landline so patients can text your existing number
6. Test by sending a text to your Emitrr number from your personal phone

Two-way texting has the highest engagement rate of any patient communication channel — most patients prefer texting over calling.`,
        tip: 'Patients increasingly prefer texting over calling. If you can only set up one feature today, make it two-way texting — it\'ll have the biggest immediate impact on patient communication.'
      },
      {
        title: 'Set Up Appointment Scheduling',
        content: `Configure your scheduling system so patients can book and you can manage appointments:

1. Go to Settings → Scheduling in Emitrr
2. Add your providers and their weekly schedules
3. Create your appointment types:
   - Name (e.g., "New Patient Exam," "Follow-Up Visit," "Cleaning")
   - Duration for each type
   - Which providers offer it
   - Color coding (optional, for visual organization)
4. Set availability windows — when patients can book
5. Configure booking rules:
   - How far in advance can patients book? (e.g., up to 60 days)
   - Minimum lead time (e.g., can't book same-day)
   - Buffer time between appointments
   - Your cancellation policy (displayed to patients)
6. Enable online booking if you want patients to self-schedule through a link or widget

You can add the online booking link to your website, Google Business Profile, or social media — similar to how NexHealth handles it.`,
        tip: 'Start with just your most common appointment types — you can always add more later. Getting the basic scheduling right is more important than covering every edge case on day one.'
      },
      {
        title: 'Configure Automated Reminders',
        content: `Set up reminder automation to reduce no-shows:

1. Go to Settings → Reminders in Emitrr
2. Create your reminder sequence — a typical setup:
   - When booked: email confirmation with appointment details
   - 48 hours before: text reminder with confirm/cancel option
   - 24 hours before: text reminder
   - 2 hours before: final text reminder (optional)
3. Customize each message:
   - Include appointment date, time, and provider name
   - Add your office address and contact number
   - Include a confirm/cancel link (Emitrr generates this automatically)
   - Add any pre-appointment instructions (e.g., "Please arrive 15 minutes early")
4. Enable confirmation tracking — see which patients confirmed, cancelled, or didn't respond
5. Set up no-show follow-up messages:
   - Automatic text after a missed appointment: "We missed you today! Would you like to reschedule?"

Text reminders at 48 and 24 hours have been shown to significantly reduce no-show rates.`,
        tip: 'Enable the confirm/cancel feature in your reminders. When patients cancel via text, the slot opens up immediately and you can fill it from your waitlist or call other patients.'
      },
      {
        title: 'Set Up The AI Chatbot',
        content: `Configure Emitrr's AI assistant to handle routine inquiries automatically:

1. Go to Settings → AI Chatbot in Emitrr
2. Enable the chatbot feature
3. Configure what it can handle:
   - Scheduling appointments (if online booking is enabled)
   - Answering FAQs ("What are your hours?" "Do you accept Delta Dental?")
   - Providing your office address and directions
   - Routing complex questions to staff
4. Train the chatbot on your practice information:
   - Office hours, location, contact info
   - Services offered
   - Insurance accepted
   - Common pre/post-visit instructions
5. Set handoff rules — when should the bot transfer to a human?
   - Clinical questions
   - Billing disputes
   - Patient complaints
   - Any request the bot can't handle
6. Test the chatbot by texting your practice number with common questions

The chatbot works via your text number — patients text your practice and the AI responds to routine questions, escalating to staff when needed.`,
        tip: 'Start simple — have the chatbot handle just 5-10 common questions at first. You can expand its capabilities over time as you see what patients ask most.'
      },
      {
        title: 'Train Staff & Go Live',
        content: `Prepare your team and launch:

1. Train your front desk staff on:
   - The Emitrr dashboard — where to find conversations, appointments, and analytics
   - Handling text conversations — responding to patient messages
   - Managing the phone system — transferring calls, checking voicemail
   - Appointment scheduling — booking, rescheduling, cancelling
2. Have everyone download the Emitrr mobile app:
   - Available for iOS and Android
   - Lets staff respond to texts and calls from anywhere
   - Useful for on-call or remote work scenarios
3. Run in parallel with your old system for 2-3 days:
   - Keep your old processes running while you verify Emitrr works correctly
   - This catches any issues before you fully switch over
4. Go fully live once you're confident everything works
5. Monitor during the first two weeks:
   - Check response times — are staff replying to texts promptly?
   - Review patient feedback — any confusion or complaints?
   - Track no-show rates — are reminders helping?
6. Review analytics weekly to optimize your communication strategy`,
        tip: 'Download the mobile app yourself and respond to a few patient messages to see the experience firsthand. This helps you give better guidance to staff and identify UX issues early.'
      }
    ],
    tips: [
      'Emitrr is HIPAA compliant — safe for all patient communication',
      'The all-in-one approach means fewer logins and less software to manage',
      'Two-way texting typically has the highest patient engagement of any channel',
      'VoIP lets staff make and receive calls from the app — they\'re not tied to the front desk',
      'The AI chatbot can handle routine inquiries and free up staff time',
      'Start with core features and add AI/automation gradually',
      'Very affordable compared to enterprise solutions — great for small practices'
    ]
  },

  'touchmd': {
    toolId: 'touchmd',
    overview: 'TouchMD is an iPad-based consultation system designed specifically for plastic surgeons and aesthetic practices. It features before/after simulation, procedure visualization, digital consent management, and patient education — all designed to enhance consultations, set realistic expectations, and increase case acceptance rates. Think of it as upgrading your consultation experience from paper brochures to an interactive, visual presentation.',
    timeEstimate: '60-120 Minutes',
    prerequisites: [
      'iPad(s) for consultation rooms (dedicated devices recommended)',
      'Your practice\'s before/after photos (or willingness to start building a photo library)',
      'Consent form documents in digital format (PDF or Word)',
      'Credit card for subscription'
    ],
    steps: [
      {
        title: 'Request A Demo & Sign Up',
        content: `TouchMD uses a consultative sales process — you'll work with their team to get set up:

1. Go to touchmd.com and click "Request Demo" or "Contact Us"
2. Fill out the form with:
   - Practice name and location
   - Your specialty (plastic surgery, aesthetics, dermatology, med spa, etc.)
   - Number of providers
   - Brief description of your current consultation process
3. Schedule a demo call — they'll show you the platform in action
4. During the demo, ask about:
   - Implementation support (many practices benefit from hands-on setup help)
   - What's included in the subscription (hardware vs. software)
   - How long implementation typically takes for a practice your size
5. After the demo, work with their sales team on pricing and agreement
6. Sign the service agreement and HIPAA BAA

TouchMD is specifically built for aesthetic practices, so the demo will be highly relevant to your workflow.`,
        tip: 'Ask about implementation support during the demo. Many practices benefit from having TouchMD\'s team help with initial content setup and workflow configuration rather than doing it all themselves.'
      },
      {
        title: 'Set Up Your iPad System',
        content: `Configure dedicated iPads for your consultation rooms:

1. Use dedicated iPads for TouchMD — don't use personal devices
   - This avoids mixing personal data with patient information
   - Ensures the iPad is always ready for consultations
   - Recommended: iPad Pro or recent iPad Air for best display quality
2. Download the TouchMD app from the App Store on each iPad
3. Log in with your practice credentials
4. Configure each iPad:
   - Assign it to a specific consultation room
   - Set up provider profiles (each surgeon/provider gets their own)
   - Configure default settings for that room's typical use
5. Connect each iPad to your practice WiFi
6. Grant necessary permissions: camera (for patient photos) and photo library access

If you have multiple consultation rooms, each should have its own iPad ready to go.`,
        tip: 'Use dedicated iPads rather than personal ones. This ensures they\'re always charged, ready, and free of personal data. Label each iPad with its room assignment.'
      },
      {
        title: 'Build Your Procedure Content Library',
        content: `Add your procedures and educational content — this is what patients see during consultations:

1. Access the TouchMD content management system (web portal or iPad)
2. Add each procedure you offer, for example:
   - Rhinoplasty
   - Breast augmentation/reduction
   - Facelifts and neck lifts
   - Body contouring (liposuction, tummy tuck)
   - Injectables (Botox, fillers)
   - Skin treatments (laser, chemical peels)
3. For each procedure, add:
   - Description and overview of the procedure
   - Benefits and expected outcomes
   - Before/after photos (your own patient photos are most impactful)
   - Recovery timeline and what to expect
   - Risks and considerations
   - Pricing information (if you show it during consultations)
4. TouchMD provides baseline educational content to customize — you don't need to create everything from scratch
5. Add your own photography, videos, and practice-specific content

The quality of your before/after photos significantly impacts consultation effectiveness. If your photo library is limited, prioritize getting a consistent photography setup.`,
        tip: 'Your own patient before/after photos are far more impactful than stock content. Invest in a simple, consistent photography setup (same background, same lighting, same angles) to build a strong photo library.'
      },
      {
        title: 'Configure Simulation & Visualization',
        content: `Set up TouchMD's before/after simulation tools:

1. Enable simulation features for relevant procedures:
   - Breast augmentation: implant sizing and visualization
   - Rhinoplasty: nose reshaping and morphing
   - Body contouring: liposuction and tummy tuck previews
2. Configure simulation parameters for each procedure:
   - Available implant sizes and profiles (for breast augmentation)
   - Morphing range and limitations (for rhinoplasty)
   - Visualization options for body procedures
3. Practice using the simulation tools yourself — get comfortable before using with patients
4. Understand and document the limitations:
   - Simulations are approximations, not guarantees
   - Always set patient expectations appropriately
   - Use simulation as a discussion tool, not a promise
5. Set up the photo capture workflow:
   - Define standard photo angles for each procedure type
   - Set lighting requirements (consistent lighting is critical)
   - Configure where photos are stored and how they're backed up

Simulations are one of TouchMD's most powerful features — they help patients visualize results and make more informed decisions.`,
        tip: 'Practice the simulation tools extensively before using them with patients. You want the interaction to feel smooth and confident, not fumbling with controls during a consultation.'
      },
      {
        title: 'Set Up Digital Consent Management',
        content: `Replace paper consent forms with TouchMD's digital process:

1. Go to the Consent Forms section in TouchMD
2. Upload your existing consent documents:
   - General surgical consent form
   - Procedure-specific consent forms (one per procedure)
   - Financial agreement / payment policy
   - Photo release and usage authorization
   - HIPAA privacy notice acknowledgment
3. Configure electronic signature capture:
   - Patients sign directly on the iPad with their finger or a stylus
   - Signatures are captured with timestamps for legal compliance
4. Set up consent workflows:
   - Which forms are required for which procedures?
   - Required signatures (patient, witness, provider)
   - Order of forms (e.g., financial first, then procedure consent)
5. Test the entire signing process on an iPad — walk through it as if you were a patient
6. Make sure completed forms are properly stored and accessible

Digital consent creates better documentation than paper — signatures are timestamped, forms are always legible, and nothing gets lost in a filing cabinet.`,
        tip: 'Go through the consent signing process yourself from the patient\'s perspective. If anything feels confusing or clunky, fix it before your next consultation. The consent experience should feel smooth and professional.'
      },
      {
        title: 'Train Your Team & Launch',
        content: `Prepare everyone who'll use TouchMD during consultations:

1. Train consultation coordinators on:
   - Basic iPad operation and TouchMD navigation
   - Walking patients through procedure content
   - Using the simulation tools during consultations
   - Capturing electronic signatures
2. Train surgeons/providers on:
   - Using TouchMD during their part of the consultation
   - Running simulations and discussing results with patients
   - Accessing patient education features
   - Taking standardized photos
3. Create a standard consultation workflow:
   - When does the iPad come out during the consultation?
   - Who handles each part (coordinator vs. surgeon)?
   - What's the sequence of content shown to patients?
4. Practice with role-play consultations — have staff play the patient
5. Start using in all consultations once the team is comfortable
6. Track key metrics to measure impact:
   - Consultation duration (should be similar or slightly longer with better outcomes)
   - Case acceptance rate (the main ROI metric)
   - Patient feedback on the consultation experience`,
        tip: 'Track your case acceptance rate before and after implementing TouchMD. This is the clearest way to measure ROI — most practices see a meaningful increase within the first few months.'
      }
    ],
    tips: [
      'TouchMD is HIPAA compliant for patient photos and clinical information',
      'Simulations help patients visualize results — but always manage expectations about limitations',
      'Your own before/after photos are significantly more impactful than stock content',
      'Digital consent streamlines paperwork and creates better audit trails',
      'iPad-based presentations appear professional and modern to patients',
      'Use TouchMD consistently in ALL consultations to maximize the investment',
      'Keep your content library updated with new procedures, photos, and recovery info'
    ]
  },

  'aesthetix-crm': {
    toolId: 'aesthetix-crm',
    overview: 'Aesthetix CRM is a specialized customer relationship management system built for plastic surgery and aesthetic practices. It features AI-powered lead scoring, automated follow-up sequences, intake automation, marketing tools, and integrations with popular aesthetic EMRs like ModMed, Nextech, and 4D EMR. Think of it as a system that ensures no prospective patient falls through the cracks — from first inquiry to consultation to procedure.',
    timeEstimate: '120-240 Minutes',
    prerequisites: [
      'Practice admin access for account setup',
      'Knowledge of your current lead sources (website forms, phone calls, social media, walk-ins)',
      'EMR login credentials if you want to integrate (ModMed, Nextech, or 4D EMR)',
      'Marketing materials and content (optional, for automated campaigns)',
      'Team members responsible for lead follow-up identified'
    ],
    steps: [
      {
        title: 'Request A Demo & Sign Up',
        content: `Aesthetix CRM uses a consultative sales process tailored to aesthetic practices:

1. Go to aesthetixcrm.com and click "Request Demo" or "Get Started"
2. Fill out the form with:
   - Practice name and specialty (plastic surgery, cosmetic, med spa, dermatology)
   - Number of providers
   - Current CRM or lead management system (if any)
   - Your EMR system (ModMed, Nextech, 4D EMR, other)
3. Schedule a demo call — they'll show you the platform with aesthetic-specific examples
4. During the demo, ask about:
   - Integration with your specific EMR
   - How lead scoring works for aesthetic procedures
   - What automated sequences are pre-built for aesthetic practices
   - Implementation timeline and support
5. Review the proposal and pricing
6. Sign the agreement including HIPAA BAA

Aesthetix CRM understands the aesthetic consultation workflow — the demo will show examples relevant to your practice type.`,
        tip: 'Come prepared with your current lead follow-up process (or lack thereof). Understanding your baseline helps the Aesthetix team show you where the biggest improvements will come from.'
      },
      {
        title: 'Configure Your Lead Sources',
        content: `Set up how prospective patient inquiries enter your CRM — you want to capture leads from every channel:

1. Website Integration:
   - Add Aesthetix lead capture forms to your website (they provide the embed code)
   - Or connect your existing website forms to Aesthetix via integration
   - Place forms on key pages: procedure pages, contact page, "Book a Consultation" page
2. Phone Integration:
   - Set up call tracking numbers for marketing campaigns
   - Configure call routing so phone leads are logged in the CRM
3. Social Media:
   - Connect Facebook and Instagram lead forms
   - Set up Messenger integration for DM inquiries
   - Instagram DMs from prospective patients flow into the CRM
4. Manual Entry:
   - Train front desk staff on adding walk-in or phone leads manually
   - Create a simple process: name, contact info, procedure interest, source
5. Test all lead sources:
   - Submit a test form on your website — does it appear in the CRM?
   - Make a test call — is it logged?
   - Send a test social media message — does it flow in?

The goal is to capture every prospective patient inquiry in one place, no matter how they reach you.`,
        tip: 'Capture leads from ALL sources — website, phone, social media, and walk-ins. Practices are often surprised to find they were losing 20-30% of inquiries because they weren\'t tracked anywhere.'
      },
      {
        title: 'Set Up AI Lead Scoring',
        content: `Configure intelligent lead prioritization so your team focuses on the most promising prospects:

1. Go to Settings → Lead Scoring in Aesthetix CRM
2. Define the criteria that indicate a high-quality lead:
   - Procedure interest: high-value procedures (e.g., rhinoplasty, breast augmentation) score higher
   - Budget indicators: if captured during intake, budget range affects score
   - Timeline urgency: "ready now" scores higher than "just researching"
   - Engagement level: opened emails, visited website multiple times, responded to texts
3. Set score thresholds that trigger different actions:
   - Hot leads (high score): immediate follow-up — call within 5 minutes
   - Warm leads (medium score): follow-up within 24 hours
   - Cold leads (low score): enter automated nurture sequence
4. Configure the AI to learn from your conversions:
   - When a lead converts to a booked consultation, the AI learns what that lead profile looks like
   - Over time, scoring accuracy improves based on your practice's actual data
5. Review and adjust scoring weights monthly during the first quarter

The AI gets smarter over time — after a few months, it'll reliably tell your team which leads to prioritize.`,
        tip: 'Speed to first contact is the single biggest factor in lead conversion for aesthetic practices. Configure hot lead alerts to notify your consultation coordinator\'s phone immediately — aim for contact within 5 minutes.'
      },
      {
        title: 'Connect Your EMR',
        content: `Integrate Aesthetix CRM with your practice management system to eliminate double data entry:

1. Go to Settings → Integrations in the Aesthetix dashboard
2. Select your EMR from the supported list:
   - ModMed (Modernizing Medicine)
   - Nextech
   - 4D EMR
   - Other systems may be available — ask during your demo
3. Follow the authentication steps for your specific EMR
4. Configure what data syncs between systems:
   - Lead → patient conversion (when a lead books, they become a patient in your EMR)
   - Appointment data flows between systems
   - Procedure history and notes
5. Test the integration thoroughly:
   - Create a test lead in Aesthetix
   - Convert them to a patient
   - Verify the record appears correctly in your EMR
   - Check that appointment data syncs both ways

EMR integration prevents duplicate entry and ensures patient records are consistent across systems.`,
        tip: 'Test the EMR integration with a fake patient before going live. Create a test lead, convert them, and verify everything syncs correctly. It\'s much easier to fix integration issues with test data.'
      },
      {
        title: 'Set Up Automated Follow-Up Sequences',
        content: `Configure automated communication so no lead is forgotten:

1. Go to Automation → Workflows in Aesthetix
2. Create follow-up sequences for different scenarios:

   New Lead Sequence:
   - Immediately: automated email acknowledging their inquiry + staff task to call
   - Day 1 (if no response): follow-up text message
   - Day 3: second follow-up email with procedure information
   - Day 7: "Still interested?" text
   - Day 14+: enter long-term nurture sequence

   Post-Consultation Sequence:
   - Same day: thank-you email + summary of what was discussed
   - Day 2: follow-up text checking for questions
   - Day 5: address common concerns for their procedure of interest
   - Day 10+: gentle check-in if they haven't booked

   Long-Term Nurture:
   - Monthly: educational content, promotions, new before/after photos
   - Seasonal: special offers tied to popular times (e.g., "Schedule your procedure now for recovery over the holidays")

3. Set up appointment reminders (confirmation, day-before, morning-of)
4. Create re-engagement campaigns for leads that went cold
5. Test all sequences end-to-end before activating`,
        tip: 'Speed to first contact is critical in aesthetic practices. Automate the immediate email/text response so prospects hear from you within seconds, even when staff is busy with patients.'
      },
      {
        title: 'Configure Intake Automation',
        content: `Streamline the intake process for consultation appointments:

1. Go to Settings → Intake in Aesthetix
2. Create digital intake forms for new consultations:
   - Contact information and demographics
   - Procedure(s) of interest
   - Basic medical history (allergies, medications, past surgeries)
   - Budget and timeline expectations
   - How they heard about your practice
   - Photo consent
3. Set up automatic sending:
   - Forms are emailed/texted as soon as a consultation is booked
   - Reminder if forms aren't completed within 24 hours
4. Configure data flow:
   - Completed intake goes into the CRM lead profile
   - If EMR is integrated, relevant info syncs to the patient chart
5. Set up staff alerts when intake is completed
   - The consultation coordinator can review information before the patient arrives
   - The surgeon has context before walking into the room

Pre-visit intake saves time during consultations and makes patients feel like you're prepared for them.`,
        tip: 'Include the "How did you hear about us?" question in every intake form. This data is gold for understanding which marketing channels are actually driving consultations.'
      },
      {
        title: 'Train Your Team & Launch',
        content: `Get your team on board and go live:

1. Train consultation coordinators — they'll use the CRM most:
   - Managing the lead queue and prioritizing hot leads
   - Responding to inquiries quickly and effectively
   - Using templates and scripts for consistent communication
   - Converting leads to booked consultations
2. Train front desk staff:
   - Entering walk-in and phone leads quickly
   - Managing appointment scheduling through the CRM
   - Processing intake forms
3. Train practice management/owners:
   - Running reports and analytics
   - Reviewing pipeline metrics (leads → consultations → booked procedures)
   - Tracking performance by lead source and team member
4. Create SOPs (standard operating procedures) for:
   - How quickly to respond to new leads
   - What to say in first contact
   - How to handle common objections
   - When to escalate to the surgeon
5. Import existing leads if migrating from another system or spreadsheets
6. Go live and monitor closely for the first 2 weeks:
   - Are leads being captured from all sources?
   - Are automated sequences firing correctly?
   - Are staff using the CRM consistently?
7. Schedule weekly pipeline review meetings to keep leads moving`,
        tip: 'Schedule a 15-minute pipeline review every week. Look at: new leads this week, leads that need follow-up, consultations booked, and leads going cold. This single meeting keeps the whole team accountable.'
      }
    ],
    tips: [
      'Aesthetix CRM is HIPAA compliant with BAA for healthcare practice use',
      'Speed to lead is critical — aim for first contact within 5 minutes of inquiry',
      'AI lead scoring improves over time as it learns from your actual conversion patterns',
      'EMR integration eliminates duplicate data entry and keeps records consistent',
      'Automated follow-up ensures no prospective patient falls through the cracks',
      'Track ROI by lead source to understand which marketing channels are worth the investment',
      'Weekly pipeline reviews keep your team accountable and leads moving forward'
    ]
  },

  'miiskin': {
    toolId: 'miiskin',
    overview: 'Miiskin provides AI-powered skin lesion analysis with patient photo documentation and change tracking over time. For dermatology practices, it enhances mole monitoring workflows by combining standardized photography with AI-assisted lesion assessment and longitudinal comparison. Miiskin offers both patient-facing apps (for self-monitoring) and professional practice tools.',
    timeEstimate: '60-120 Minutes',
    prerequisites: [
      'Dermatology practice credentials',
      'Smartphones or tablets for capturing patient photos',
      'Patient consent forms for photography and AI analysis',
      'Staff time for training on photo capture protocols'
    ],
    steps: [
      {
        title: 'Contact Miiskin & Evaluate Fit',
        content: `Miiskin offers different products — make sure you're setting up the right one for your practice:

1. Go to miiskin.com
2. Navigate to the Professional/Practice section (not the consumer app)
3. Click "Request Demo" or "Contact Us"
4. Provide your practice information:
   - Practice name and location
   - Number of dermatologists/providers
   - Current photo documentation process (how do you capture and store patient photos today?)
   - What you're looking for: AI analysis, photo documentation, patient engagement, or all of the above
5. Schedule a demonstration with their team
6. During the demo, clarify:
   - What's included in the practice plan vs. the patient-facing app
   - How AI analysis works and its clinical validation
   - Integration options with your EMR/EHR
   - Pricing structure for your practice size
7. Review pricing, sign the agreement, and execute the HIPAA BAA

Miiskin has both a consumer app (patients tracking their own moles) and a professional platform. Make sure you're evaluating the professional version.`,
        tip: 'Clarify the difference between Miiskin\'s consumer app and professional practice tools during the demo. The professional platform has features like multi-patient management, clinical-grade analysis, and EMR integration that the consumer app doesn\'t.'
      },
      {
        title: 'Set Up Your Practice Account',
        content: `Configure your Miiskin practice system:

1. Receive your practice account credentials from Miiskin (they'll set up the admin account)
2. Log into the practice portal
3. Add your providers:
   - Names and credentials (MD, DO, etc.)
   - Specialties or areas of focus
   - Who should have access to what features
4. Configure practice-wide settings:
   - Photo storage preferences (local vs. cloud, resolution settings)
   - AI analysis settings (sensitivity level, what types of analysis to run)
   - Report formats (how AI findings are presented)
   - Notification preferences
5. Set up user permissions for your team:
   - Who can capture photos (medical assistants, nurses, techs)
   - Who can access AI analysis results (dermatologists, PAs)
   - Who can manage patient data (admin staff)

Clear role assignment prevents confusion and ensures proper data access controls.`,
        tip: 'Assign clear roles from the start — who captures photos, who reviews AI analysis, and who manages patient records. This prevents bottlenecks and ensures consistent workflows.'
      },
      {
        title: 'Establish Your Photo Documentation Protocol',
        content: `Consistent photography is the foundation of everything else — AI analysis and change tracking are only as good as the photos:

1. Define your standard photo protocol:
   - Full body mapping views: front, back, left side, right side (for baseline documentation)
   - Close-up lesion shots: centered, well-lit, with a ruler for scale
   - Dermoscopy images: if you use dermoscopy attachments with your phone/tablet
2. Set up your capture devices:
   - Download the Miiskin app on all devices used for photo capture
   - Configure camera settings for clinical photography (highest resolution, no filters)
   - Test image quality with sample photos
3. Create a consistent capture environment:
   - Same background for each photo station (neutral, non-reflective)
   - Same lighting conditions (bright, even lighting — avoid harsh shadows)
   - Same distance from patient for each body region
4. Define your workflow:
   - When are photos taken? (during check-in, during the exam, or both)
   - Who takes the photos? (medical assistant, nurse, technician)
   - What's the sequence? (full body first, then targeted lesions)
5. Document your protocol so all staff follow it consistently

The key principle: if two photos of the same lesion taken 6 months apart look different because of lighting or angle changes, the AI can't reliably detect actual changes. Consistency matters more than perfection.`,
        tip: 'Create a one-page photo protocol cheat sheet and post it in every exam room. Include standard angles, lighting tips, and the order of shots. Consistent photos are more valuable than perfect photos.'
      },
      {
        title: 'Understand AI Lesion Analysis Capabilities & Limitations',
        content: `Before using AI analysis with patients, understand what it can and can't do:

1. Review Miiskin's AI capabilities:
   - Lesion detection: identifies and segments skin lesions in photos
   - Risk assessment: provides risk indicators based on visual features (asymmetry, border, color, etc.)
   - Change detection: compares images over time to flag changes in size, shape, or color
2. Understand the limitations — this is critical:
   - AI analysis is clinical decision support, NOT a diagnosis
   - Clinical judgment always takes precedence over AI scores
   - False positives happen (AI flags something benign as concerning)
   - False negatives happen (AI misses something that is concerning)
   - The AI is a second opinion, not a replacement for dermatologist expertise
3. Configure AI settings for your practice:
   - Sensitivity level: higher sensitivity catches more but generates more false positives
   - Alert thresholds: what score triggers a flag for review
   - Report preferences: how detailed the AI report should be
4. Decide how to communicate AI findings to patients:
   - Will you show patients the AI analysis? (some practices do, some don't)
   - How will you frame it? ("The AI helps us track changes" vs. sharing risk scores)
   - Prepare language for when AI and clinical assessment disagree

Document your practice's policy on AI-assisted diagnosis for compliance purposes.`,
        tip: 'Create a brief internal policy document that states: "Miiskin AI analysis is used as clinical decision support. All diagnostic decisions are made by the treating dermatologist." This protects your practice and sets clear expectations.'
      },
      {
        title: 'Set Up Patient Onboarding & Consent',
        content: `Configure how patients are enrolled in the Miiskin system:

1. Create or update your consent forms to include:
   - Photography consent: permission to capture and store clinical photos
   - Data storage consent: where and how photos are stored
   - AI analysis consent: permission to use AI tools on their images
   - If applicable: consent for photos to be used in educational/before-after materials (separate form)
2. If using patient-facing features (patients tracking moles at home):
   - Set up patient account creation workflow
   - Configure what patients can see (their own photos, AI summaries, or just appointment reminders)
   - Create clear instructions for patients on how to use the Miiskin app at home
3. Set up patient communications:
   - Appointment reminders for follow-up skin checks
   - Instructions for at-home self-monitoring (if applicable)
   - How patients should contact the office if they notice changes between visits
4. Create patient education materials explaining:
   - What the AI analysis means (and doesn't mean)
   - Why consistent monitoring is important
   - Warning signs to watch for between visits

Clear consent and education prevents misunderstandings about what AI can and cannot do.`,
        tip: 'Keep AI consent language simple and honest. Something like: "We use AI tools to help track changes in your skin over time. These tools assist your dermatologist but do not replace their clinical judgment." Patients appreciate transparency.'
      },
      {
        title: 'Train Your Clinical Team',
        content: `Everyone involved in the photo documentation and analysis workflow needs proper training:

1. Train photo capture staff (medical assistants, nurses, technicians):
   - Proper technique: angles, lighting, distance, positioning
   - Using the Miiskin app for consistent captures
   - Following your documented photo protocol
   - Handling dermoscopy attachments (if used)
2. Train dermatologists and providers:
   - How to interpret AI risk scores and change detection reports
   - Integrating AI findings with clinical assessment
   - Documenting decisions when AI and clinical judgment disagree
   - Communicating AI findings to patients appropriately
3. Train on patient communication:
   - Explaining the AI system to patients in simple terms
   - Managing expectations (AI is a tool, not a diagnosis)
   - Handling questions about AI accuracy
4. Create quick reference guides:
   - Photo capture protocol (one-pager for exam rooms)
   - AI score interpretation guide (what the numbers mean)
   - Patient communication scripts for common questions
5. Practice with test cases before using with real patients

Hands-on practice builds confidence — have staff practice the full workflow with volunteer "patients" before launch.`,
        tip: 'Schedule a hands-on practice session where staff photograph each other using the protocol. This builds confidence with the technology and catches workflow issues before real patients are involved.'
      },
      {
        title: 'Implement Longitudinal Monitoring',
        content: `Set up the change tracking workflow — this is where Miiskin provides the most value:

1. Establish baseline documentation for each patient:
   - Full body photos at the initial skin check visit
   - Individual lesion documentation for any areas of concern
   - Dermoscopy images for lesions that warrant closer monitoring
2. Configure follow-up protocols:
   - Recommended intervals based on risk level (3 months, 6 months, 12 months)
   - Set up automatic appointment reminders for follow-up skin checks
   - Configure change detection alerts (notify you if AI detects changes between visits)
3. Use comparison features during follow-up visits:
   - Side-by-side photo comparison of the same lesion over time
   - AI-powered change detection highlighting differences
   - Measurement tools for tracking lesion size
4. Document your clinical decisions:
   - Record whether you agree or disagree with AI change detection
   - Note your clinical rationale for monitoring, biopsy, or no action
   - This creates an audit trail of your clinical reasoning

Consistent baseline documentation is the foundation. Change tracking is only meaningful if the initial photos are standardized.`,
        tip: 'Invest time in thorough baseline documentation at each patient\'s first skin check. Comprehensive initial photos make every future comparison more valuable and accurate.'
      }
    ],
    tips: [
      'Miiskin is HIPAA compliant for clinical imaging and patient health data',
      'AI analysis is clinical decision support — always apply dermatologist clinical judgment',
      'Consistent photo technique is critical for accurate change tracking over time',
      'Patient engagement with at-home self-monitoring can improve outcomes and early detection',
      'Document AI findings alongside your clinical assessment for proper medical records',
      'Regular follow-up imaging enables meaningful longitudinal comparisons',
      'Clear patient consent covering photography and AI analysis is essential before starting'
    ]
  },

  'fotofinder': {
    toolId: 'fotofinder',
    overview: 'FotoFinder provides clinical-grade total body mapping and AI lesion detection with the Moleanalyzer pro system. Validated at Heidelberg University with performance comparable to experienced dermatologists, it\'s designed for practices that need the highest standard of skin imaging and AI-assisted diagnosis. This is enterprise medical equipment — expect a significant capital investment, professional installation, and comprehensive training.',
    timeEstimate: '10,000-20,000 Minutes',
    prerequisites: [
      'Dermatology practice with dedicated space for a total body scanner (if purchasing one)',
      'Budget approved for enterprise medical imaging equipment',
      'IT infrastructure capable of storing and backing up high-resolution medical images',
      'Staff availability for training (typically 1-2 days)',
      'Executive/partner approval for the capital investment'
    ],
    steps: [
      {
        title: 'Request A Consultation & Evaluate The Investment',
        content: `FotoFinder is enterprise medical equipment — this is a significant purchase that requires careful evaluation:

1. Go to fotofinder.de/en or contact the regional distributor for your area
2. Request a formal consultation
3. Provide detailed information about your practice:
   - Practice size and monthly patient volume for skin checks
   - Current imaging capabilities (what you use now)
   - Specific needs: total body mapping, dermoscopy, AI analysis, or all three
   - Physical space available for equipment (room dimensions, electrical capacity)
4. Schedule an on-site or virtual demonstration
   - If possible, visit a practice that already uses FotoFinder to see it in action
5. Receive a detailed proposal including:
   - Hardware specifications (total body scanner, dermoscopy devices, workstation)
   - Software licensing (Moleanalyzer pro AI, patient management system)
   - Installation and setup
   - Training program
   - Ongoing support and maintenance
   - Total cost of ownership

This is a major capital investment — involve practice leadership and potentially a financial advisor in the evaluation.`,
        tip: 'Ask to visit a nearby practice that already uses FotoFinder. Seeing the equipment in action in a real clinical setting is far more informative than a demo video or brochure.'
      },
      {
        title: 'Plan Your Implementation',
        content: `Prepare your practice for equipment installation — this typically takes 1-2 weeks of planning:

1. Work with FotoFinder's implementation team to create a project plan
2. Prepare the physical space:
   - Dedicated room for the total body scanner (if purchasing one)
   - The room needs adequate space for the equipment plus patient positioning
   - Lighting may need modification (FotoFinder will specify requirements)
   - Privacy considerations: the room should be fully enclosed with a door
   - Electrical requirements: verify outlets and capacity with your facilities team
3. IT preparation:
   - Network connectivity in the imaging room
   - Data storage solution for high-resolution images (these files are large)
   - EMR integration planning — how will images connect to patient records?
   - Backup systems for image data (HIPAA requires data backup)
   - Bandwidth: uploading large image files may require network upgrades
4. Staff scheduling:
   - Block time for training (typically 1-2 full days)
   - Identify who will be trained: imaging tech, dermatologists, front desk
5. Set a timeline with specific milestones and a go-live date

Space and IT preparation often take longer than expected — start this planning before equipment arrives.`,
        tip: 'Start IT and room preparation as early as possible. These are the most common sources of delay. Having the room ready and network configured before equipment arrives can save a week or more.'
      },
      {
        title: 'Equipment Installation',
        content: `FotoFinder sends professional technicians for installation — here's what to expect:

1. Coordinate the installation date with FotoFinder and your practice schedule
   - Plan for the imaging room to be unavailable for 1-2 days during setup
2. FotoFinder technicians will handle:
   - Physical installation of the total body scanner (if purchased)
   - Setup of dermoscopy devices and docking stations
   - Software installation on your workstation(s)
   - Network configuration and connection to your systems
   - Equipment calibration for accurate imaging
3. After installation, they'll verify everything is working:
   - Total body scanner captures complete set of standardized views
   - Dermoscopy devices produce clinical-quality images
   - Software interface is responsive and correctly configured
   - Images are stored in the right location with proper backup
   - Moleanalyzer pro AI analysis is functioning
4. Document the equipment for warranty and maintenance records
5. Get contact information for technical support

Have your key staff present during installation — watching the setup process helps them understand the system from day one.`,
        tip: 'Have at least one dermatologist and your lead imaging tech present during the installation. They\'ll pick up details about equipment operation that are hard to learn later.'
      },
      {
        title: 'Configure Moleanalyzer Pro AI',
        content: `Set up the AI analysis capabilities — this is FotoFinder's core differentiator:

1. Review Moleanalyzer pro's features with the FotoFinder team:
   - AI-powered lesion analysis (trained on clinically validated datasets)
   - Risk scoring for individual lesions
   - Comparison tools for longitudinal monitoring
   - Performance validation data from Heidelberg University
2. Configure AI settings for your practice:
   - Sensitivity thresholds: higher sensitivity catches more but increases false positives
   - Alert preferences: how and when are you notified of high-risk findings
   - Report formats: choose the level of detail in AI reports
   - Display options: what information shows on screen during patient encounters
3. Understand the clinical validation:
   - Review the Heidelberg University study results
   - Understand how Moleanalyzer pro's performance compares to dermatologists
   - Know the sensitivity and specificity numbers for your documentation
4. Establish your clinical protocols for AI-assisted diagnosis:
   - How AI findings factor into your clinical decision-making
   - Documentation standards when AI and clinical judgment disagree
   - Quality assurance: periodic review of AI accuracy in your patient population

The Moleanalyzer pro AI is clinically validated, but it's still a decision support tool — your clinical judgment remains the final authority.`,
        tip: 'Review the Heidelberg University validation studies so you can confidently explain the AI\'s capabilities and limitations to patients and colleagues. Understanding the evidence builds trust in the technology.'
      },
      {
        title: 'Integrate With Your EMR',
        content: `Connect FotoFinder to your practice management system for a seamless workflow:

1. Work with both FotoFinder's team and your EMR vendor to plan the integration
2. Configure the data flow:
   - Patient demographics: pull from your EMR so you don't re-enter patient info
   - Image storage: images should be accessible from within patient charts
   - AI reports: findings should attach to the relevant patient encounter
   - Visit documentation: imaging data should link to your clinical notes
3. Set up the clinical workflow:
   - When a patient checks in, their record is available in FotoFinder
   - Images captured during the visit are automatically associated with the patient
   - AI analysis results attach to the encounter
   - Dermatologist can view images and AI findings from within the EMR
4. Test the integration thoroughly:
   - Create test patients and capture images
   - Verify images appear correctly in the EMR
   - Check that AI reports are properly attached
   - Test the workflow with actual staff to identify friction points
5. Document the integration for IT support and troubleshooting

EMR integration is what makes FotoFinder a seamless part of your clinical workflow rather than a separate system.`,
        tip: 'Don\'t go live with patients until the EMR integration is fully tested and working. A broken integration creates more work than no integration at all.'
      },
      {
        title: 'Staff Training',
        content: `FotoFinder provides comprehensive professional training — take full advantage:

1. FotoFinder's training program typically covers:
   - Equipment operation: using the scanner, dermoscopy devices, and workstation
   - Software navigation: patient management, image capture, AI analysis
   - AI interpretation: understanding Moleanalyzer pro scores and reports
   - Maintenance procedures: daily checks, cleaning, calibration
2. Train each role on their specific responsibilities:
   - Imaging technicians: photo capture protocols, equipment operation, patient positioning, quality checks
   - Dermatologists: AI interpretation, clinical workflow integration, patient communication about AI findings
   - Front desk: scheduling imaging appointments, patient preparation instructions
3. Practice with test patients:
   - Run through the complete workflow with volunteer "patients" (staff or family)
   - Capture a full set of images, run AI analysis, review results
   - Time the workflow to understand scheduling needs
4. Create quick reference guides for daily use:
   - Equipment startup/shutdown procedure
   - Photo capture protocol
   - Troubleshooting common issues
   - Contact info for FotoFinder support
5. Consider certification if FotoFinder offers it for your staff`,
        tip: 'Invest in thorough training — the system\'s clinical and financial value depends entirely on proper use. Rushing through training to start seeing patients sooner almost always backfires.'
      },
      {
        title: 'Pilot Phase & Full Deployment',
        content: `Start with a controlled rollout before seeing your full patient volume:

1. Pilot phase (1-2 weeks):
   - Start with selected patients who need skin checks
   - Schedule extra time per patient while staff builds proficiency (expect 30-50% longer initially)
   - Have backup support available for equipment questions
   - Capture feedback from patients, imaging techs, and dermatologists daily
2. Address issues discovered during the pilot:
   - Adjust workflows that don't work smoothly
   - Fix any integration problems
   - Refine AI interpretation protocols
   - Update scheduling templates based on actual time per patient
3. Full deployment:
   - Expand to all appropriate patients
   - Return to standard scheduling (times should normalize as staff gains experience)
   - Implement standard workflows documented during the pilot
4. Monitor outcomes long-term:
   - Detection rates: are you catching more concerning lesions?
   - Patient satisfaction: do patients value the technology?
   - Workflow efficiency: how does imaging time per patient trend over time?
   - AI accuracy: periodic review of AI findings vs. clinical outcomes
5. Ongoing optimization:
   - Stay current on software updates from FotoFinder
   - Review and update clinical protocols as AI capabilities evolve
   - Schedule regular equipment maintenance per FotoFinder's recommendations

The pilot phase is invaluable — it catches issues before they affect your full patient volume and gives staff time to build confidence.`,
        tip: 'Schedule extra time per patient during the pilot (add 15-20 minutes). Staff will get faster with practice, and rushing during the learning phase leads to poor photo quality and frustrated staff.'
      }
    ],
    tips: [
      'FotoFinder is a significant capital investment — evaluate ROI carefully before purchasing',
      'Moleanalyzer pro AI is clinically validated but supplements, not replaces, clinical judgment',
      'Total body mapping enables meaningful longitudinal tracking that isn\'t possible with ad-hoc photos',
      'Proper training is essential — the system\'s value depends entirely on consistent, correct use',
      'EMR integration significantly improves workflow — prioritize getting this working before go-live',
      'Space and IT preparation often take longer than expected — start early',
      'Regular equipment maintenance ensures consistent image quality over time'
    ]
  }
};

export function getGuideByToolId(toolId: string): Guide | undefined {
  return guides[toolId];
}

export function hasGuide(toolId: string): boolean {
  return toolId in guides;
}
